// Tail-spend seed data, ported from plans/tail spend/prototype.html.
//
// One big seed file rather than several — the data is heavily cross-linked
// (suppliers ↔ deals ↔ audit ↔ notifications) and splitting it across files
// just made the ids harder to follow.

import {
    Benchmark,
    Category,
    categoryId,
    Connector,
    ConnectorEvent,
    ConnectorEventDirection,
    ConnectorId,
    connectorId,
    Deal,
    dealId,
    DecisionPrompt,
    DecisionResolution,
    Escalation,
    GuardrailPolicy,
    Lever,
    Notification,
    Outcome,
    OverviewNote,
    PolicyId,
    PortfolioReport,
    policyId,
    Supplier,
    SupplierId,
    supplierId,
    ThreadItem,
    Tone,
    usd,
    Workspace,
    AuditEvent,
} from "./types";
import { DealState } from "./lifecycle";
import { dealBadge, dealResultLabel } from "./api";

// ---------------------------------------------------------------------------
// Categories & policies
// ---------------------------------------------------------------------------
const POLICY_MRO: PolicyId = policyId("pol-mro");
const POLICY_PACKAGING: PolicyId = policyId("pol-packaging");
const POLICY_LOGISTICS: PolicyId = policyId("pol-logistics");
const POLICY_PPE: PolicyId = policyId("pol-ppe");
const POLICY_RND: PolicyId = policyId("pol-rnd");
const POLICY_OFFICE: PolicyId = policyId("pol-office");
const POLICY_FACILITIES: PolicyId = policyId("pol-facilities");
const POLICY_LAB: PolicyId = policyId("pol-lab");
const POLICY_IT: PolicyId = policyId("pol-it");
const POLICY_JANITORIAL: PolicyId = policyId("pol-jan");

const CAT_MRO = categoryId("cat-mro");
const CAT_PACKAGING = categoryId("cat-packaging");
const CAT_LOGISTICS = categoryId("cat-logistics");
const CAT_PPE = categoryId("cat-ppe");
const CAT_RND = categoryId("cat-rnd");
const CAT_OFFICE = categoryId("cat-office");
const CAT_FACILITIES = categoryId("cat-facilities");
const CAT_LAB = categoryId("cat-lab");
const CAT_IT = categoryId("cat-it");
const CAT_JANITORIAL = categoryId("cat-janitorial");

const categories: Category[] = [
    { id: CAT_MRO, key: "mro", name: "MRO & Indirect Materials", policyId: POLICY_MRO },
    { id: CAT_PACKAGING, key: "packaging", name: "Packaging", policyId: POLICY_PACKAGING },
    { id: CAT_LOGISTICS, key: "logistics", name: "Freight & Logistics", policyId: POLICY_LOGISTICS },
    { id: CAT_PPE, key: "ppe", name: "PPE / Safety", policyId: POLICY_PPE },
    { id: CAT_RND, key: "rnd_consumables", name: "R&D Consumables", policyId: POLICY_RND },
    { id: CAT_OFFICE, key: "office_supplies", name: "Office Supplies", policyId: POLICY_OFFICE },
    { id: CAT_FACILITIES, key: "facilities", name: "Facilities", policyId: POLICY_FACILITIES },
    { id: CAT_LAB, key: "lab_services", name: "Lab Services", policyId: POLICY_LAB },
    { id: CAT_IT, key: "it_hardware", name: "IT Hardware", policyId: POLICY_IT },
    { id: CAT_JANITORIAL, key: "janitorial", name: "Janitorial", policyId: POLICY_JANITORIAL },
];

const standardCap = usd(150_000);
const standardESign = { min: usd(100_000), max: usd(150_000) };

const policies: GuardrailPolicy[] = [
    {
        id: POLICY_MRO,
        categoryId: CAT_MRO,
        active: true,
        summary: [
            ["Autonomous scope", "$150,000"],
            ["Auto-execute", "When terms are inside the envelope"],
            ["Buyer e-sign first", "$100,000 – $150,000"],
            ["Hand to strategic above", "$150,000"],
            ["Walk-away floor", "Price ≥ −2% OR terms ≥ +15 days OR rebate ≥ 2%"],
            ["Levers in play", "Unit price · payment terms · rebate · auto-renewal removal"],
            ["Required clauses", "Termination for convenience (30d) · audit rights"],
            ["Concession ladder", "Up to 2 reciprocal concessions before escalation"],
        ],
        versions: [
            {
                version: 1,
                startDate: "Jun 1, 2025",
                endDate: "Nov 30, 2025",
                summary: [
                    ["Autonomous scope", "$100,000"],
                    ["Auto-execute", "When terms are inside the envelope"],
                    ["Buyer e-sign first", "$75,000 – $100,000"],
                    ["Hand to strategic above", "$100,000"],
                    ["Walk-away floor", "Price ≥ −1% OR terms ≥ +10 days"],
                    ["Levers in play", "Unit price · payment terms"],
                    ["Required clauses", "Termination for convenience (60d)"],
                    ["Concession ladder", "Up to 1 reciprocal concession before escalation"],
                ],
            },
            {
                version: 2,
                startDate: "Dec 1, 2025",
                endDate: "Mar 31, 2026",
                summary: [
                    ["Autonomous scope", "$125,000"],
                    ["Auto-execute", "When terms are inside the envelope"],
                    ["Buyer e-sign first", "$90,000 – $125,000"],
                    ["Hand to strategic above", "$125,000"],
                    ["Walk-away floor", "Price ≥ −1.5% OR terms ≥ +12 days OR rebate ≥ 1%"],
                    ["Levers in play", "Unit price · payment terms · rebate"],
                    ["Required clauses", "Termination for convenience (30d) · audit rights"],
                    ["Concession ladder", "Up to 2 reciprocal concessions before escalation"],
                ],
            },
            {
                version: 3,
                startDate: "Apr 1, 2026",
                summary: [
                    ["Autonomous scope", "$150,000"],
                    ["Auto-execute", "When terms are inside the envelope"],
                    ["Buyer e-sign first", "$100,000 – $150,000"],
                    ["Hand to strategic above", "$150,000"],
                    ["Walk-away floor", "Price ≥ −2% OR terms ≥ +15 days OR rebate ≥ 2%"],
                    ["Levers in play", "Unit price · payment terms · rebate · auto-renewal removal"],
                    ["Required clauses", "Termination for convenience (30d) · audit rights"],
                    ["Concession ladder", "Up to 2 reciprocal concessions before escalation"],
                ],
            },
        ],
        autonomousCap: standardCap,
        buyerESignBand: standardESign,
        handToStrategicAbove: standardCap,
        walkAwayFloor: { unitPriceWorstAcceptablePct: -2, minPaymentTermsExtensionDays: 15, minVolumeRebatePct: 2 },
        leversInPlay: ["unit_price", "payment_terms", "volume_rebate", "auto_renewal_removal"],
        requiredClauses: ["Termination for convenience (30d)", "Audit rights"],
        concessionLadderDepth: 2,
        minConfidenceForAutoExecute: "medium",
    },
    {
        id: POLICY_PACKAGING,
        categoryId: CAT_PACKAGING,
        active: true,
        summary: [
            ["Autonomous scope", "$150,000"],
            ["Auto-execute", "When terms are inside the envelope"],
            ["Buyer e-sign first", "$80,000 – $150,000"],
            ["Hand to strategic above", "$150,000"],
            ["Walk-away floor", "Price ≥ −2% (commodity-linked) OR terms ≥ +15 days"],
            ["Levers in play", "Unit price · payment terms · index pass-through cap"],
            ["Required clauses", "Commodity-index transparency · 30-day termination"],
            ["Concession ladder", "Up to 2 reciprocal concessions"],
        ],
        autonomousCap: standardCap,
        buyerESignBand: { min: usd(80_000), max: usd(150_000) },
        handToStrategicAbove: standardCap,
        walkAwayFloor: { unitPriceWorstAcceptablePct: -2, minPaymentTermsExtensionDays: 15 },
        leversInPlay: ["unit_price", "payment_terms", "index_passthrough_cap"],
        requiredClauses: ["Commodity-index transparency", "30-day termination"],
        concessionLadderDepth: 2,
    },
    {
        id: POLICY_LOGISTICS,
        categoryId: CAT_LOGISTICS,
        active: true,
        summary: [
            ["Autonomous scope", "$150,000"],
            ["Auto-execute", "When terms are inside the envelope"],
            ["Buyer e-sign first", "$75,000 – $150,000"],
            ["Hand to strategic above", "$150,000"],
            ["Walk-away floor", "Rate ≥ −3% OR accessorial caps agreed"],
            ["Levers in play", "Lane rate · fuel-surcharge cap · accessorials · payment terms"],
            ["Required clauses", "Fuel-index cap · service SLA · 30-day termination"],
            ["Concession ladder", "Up to 2 reciprocal concessions"],
        ],
        autonomousCap: standardCap,
        buyerESignBand: { min: usd(75_000), max: usd(150_000) },
        handToStrategicAbove: standardCap,
        walkAwayFloor: { unitPriceWorstAcceptablePct: -3, custom: ["Accessorial caps agreed"] },
        leversInPlay: ["service_rate", "fuel_surcharge_cap", "accessorials", "payment_terms"],
        requiredClauses: ["Fuel-index cap", "Service SLA", "30-day termination"],
        concessionLadderDepth: 2,
    },
    {
        id: POLICY_PPE,
        categoryId: CAT_PPE,
        active: true,
        summary: [
            ["Autonomous scope", "$150,000"],
            ["Auto-execute", "When terms are inside the envelope"],
            ["Buyer e-sign first", "$100,000 – $150,000"],
            ["Hand to strategic above", "$150,000"],
            ["Walk-away floor", "Price ≥ −2% OR terms ≥ +15 days"],
            ["Levers in play", "Unit price · payment terms · volume rebate"],
            ["Required clauses", "Certification compliance · 30-day termination · audit rights"],
            ["Concession ladder", "Up to 2 reciprocal concessions"],
        ],
        autonomousCap: standardCap,
        buyerESignBand: standardESign,
        handToStrategicAbove: standardCap,
        walkAwayFloor: { unitPriceWorstAcceptablePct: -2, minPaymentTermsExtensionDays: 15 },
        leversInPlay: ["unit_price", "payment_terms", "volume_rebate"],
        requiredClauses: ["Certification compliance", "30-day termination", "Audit rights"],
        concessionLadderDepth: 2,
    },
    // Categories that don't have a dedicated policy in the prototype still need
    // a record so suppliers in those categories resolve.
    ...["pol-rnd", "pol-office", "pol-facilities", "pol-lab", "pol-it", "pol-jan"].map(
        (id, idx): GuardrailPolicy => ({
            id: id as PolicyId,
            categoryId: [CAT_RND, CAT_OFFICE, CAT_FACILITIES, CAT_LAB, CAT_IT, CAT_JANITORIAL][idx],
            active: true,
            summary: [
                ["Autonomous scope", "$150,000"],
                ["Auto-execute", "When terms are inside the envelope"],
                ["Hand to strategic above", "$150,000"],
                ["Walk-away floor", "Price ≥ −2% OR terms ≥ +15 days"],
            ],
            autonomousCap: standardCap,
            buyerESignBand: standardESign,
            handToStrategicAbove: standardCap,
            walkAwayFloor: { unitPriceWorstAcceptablePct: -2, minPaymentTermsExtensionDays: 15 },
            leversInPlay: ["unit_price", "payment_terms"],
            requiredClauses: ["30-day termination", "Audit rights"],
            concessionLadderDepth: 2,
        })
    ),
];

// ---------------------------------------------------------------------------
// Suppliers
// ---------------------------------------------------------------------------
const SUP_CRESCENT: SupplierId = supplierId("sup-crescent");
const SUP_VERTEX: SupplierId = supplierId("sup-vertex");
const SUP_NIMBUS: SupplierId = supplierId("sup-nimbus");
const SUP_APEX: SupplierId = supplierId("sup-apex");
const SUP_BRIGHTWAY: SupplierId = supplierId("sup-brightway");
const SUP_CARDINAL: SupplierId = supplierId("sup-cardinal");
const SUP_ORION: SupplierId = supplierId("sup-orion");
const SUP_ORBIT: SupplierId = supplierId("sup-orbit");
const SUP_DELTA: SupplierId = supplierId("sup-delta");
const SUP_MERIDIAN: SupplierId = supplierId("sup-meridian");
const SUP_PINNACLE: SupplierId = supplierId("sup-pinnacle");
const SUP_SUMMIT: SupplierId = supplierId("sup-summit");
const SUP_HARBOR: SupplierId = supplierId("sup-harbor");
const SUP_POLARIS: SupplierId = supplierId("sup-polaris");
const SUP_ASPEN: SupplierId = supplierId("sup-aspen");
const SUP_BEACON: SupplierId = supplierId("sup-beacon");
const SUP_COASTLINE: SupplierId = supplierId("sup-coastline");
// 10 in-flight suppliers (active deals)
const SUP_MAGNOLIA: SupplierId = supplierId("sup-magnolia");
const SUP_SENTINEL: SupplierId = supplierId("sup-sentinel");
const SUP_COBALT: SupplierId = supplierId("sup-cobalt");
const SUP_CYPRESS: SupplierId = supplierId("sup-cypress");
const SUP_NORTHWIND: SupplierId = supplierId("sup-northwind");
const SUP_IRONWOOD: SupplierId = supplierId("sup-ironwood");
const SUP_PACIFIC: SupplierId = supplierId("sup-pacific");
const SUP_QUARTZLINE: SupplierId = supplierId("sup-quartzline");
const SUP_VISTA: SupplierId = supplierId("sup-vista");
const SUP_GRANITE: SupplierId = supplierId("sup-granite");

const suppliers: Supplier[] = [
    {
        id: SUP_CRESCENT,
        name: "Crescent Industrial Supply",
        primaryEmail: "m.whelan@crescent-ind.com",
        categoryId: CAT_MRO,
        annualSpend: usd(87_400),
        grade: "B",
        hasLiveAgreement: true,
        nextRenewal: { id: "ren-crescent", kind: "contract_expiry", window: "Dec 2027" },
        lastNegotiatedLabel: "Today",
        termsSummary: "−3% · Net 45 · 2% rebate",
        currentDealId: dealId("deal-crescent"),
    },
    {
        id: SUP_VERTEX,
        name: "Vertex Safety Equip.",
        primaryEmail: "orders@vertexsafety.com",
        categoryId: CAT_PPE,
        annualSpend: usd(56_800),
        grade: "B",
        hasLiveAgreement: true,
        netNewAgreement: true,
        nextRenewal: { id: "ren-vertex", kind: "contract_expiry", window: "Oct 2027" },
        lastNegotiatedLabel: "Today",
        termsSummary: "−5.5% · Net 60",
        currentDealId: dealId("deal-vertex"),
    },
    {
        id: SUP_NIMBUS,
        name: "Nimbus IT Reseller",
        primaryEmail: "sales@nimbusit.com",
        categoryId: CAT_IT,
        annualSpend: usd(128_000),
        grade: "B",
        hasLiveAgreement: true,
        nextRenewal: { id: "ren-nimbus", kind: "contract_expiry", window: "Sep 2026" },
        lastNegotiatedLabel: "Sep 2026",
        termsSummary: "−5.3% · Net 45",
        currentDealId: dealId("deal-nimbus"),
    },
    {
        id: SUP_APEX,
        name: "Apex Lab Reagents",
        primaryEmail: "sales@apexreagents.com",
        categoryId: CAT_RND,
        annualSpend: usd(92_300),
        grade: "C",
        hasLiveAgreement: true,
        nextRenewal: { id: "ren-apex", kind: "contract_expiry", window: "In 21 days" },
        lastNegotiatedLabel: "Oct 2024",
        termsSummary: "Net 30 · 2024 catalog pricing",
        currentDealId: dealId("deal-apex"),
    },
    {
        id: SUP_BRIGHTWAY,
        name: "Brightway Logistics",
        primaryEmail: "contracts@brightwaylog.com",
        categoryId: CAT_LOGISTICS,
        annualSpend: usd(310_000),
        hasLiveAgreement: true,
        nextRenewal: { id: "ren-brightway", kind: "contract_expiry", window: "In 58 days" },
        lastNegotiatedLabel: "Reassigned",
        termsSummary: "Net 30 · standard freight rates",
        currentDealId: dealId("deal-brightway"),
    },
    {
        id: SUP_CARDINAL,
        name: "Cardinal Pallet & Crate",
        primaryEmail: "deals@cardinalpc.com",
        categoryId: CAT_PACKAGING,
        annualSpend: usd(74_200),
        grade: "B-",
        hasLiveAgreement: true,
        nextRenewal: { id: "ren-cardinal", kind: "contract_expiry", window: "In 34 days" },
        lastNegotiatedLabel: "Nov 2024",
        termsSummary: "Net 45 · 2024 annual pricing",
        currentDealId: dealId("deal-cardinal"),
    },
    {
        id: SUP_ORION,
        name: "Orion Office & Print",
        primaryEmail: "ar@orionprint.com",
        categoryId: CAT_OFFICE,
        annualSpend: usd(38_900),
        grade: "C+",
        hasLiveAgreement: true,
        nextRenewal: { id: "ren-orion", kind: "contract_expiry", window: "In 12 days" },
        lastNegotiatedLabel: "Nov 2024",
        termsSummary: "Net 30 · 2024 catalog pricing",
        currentDealId: dealId("deal-orion"),
    },
    {
        id: SUP_ORBIT,
        name: "Orbit Janitorial",
        primaryEmail: "accounts@orbitjanitorial.com",
        categoryId: CAT_FACILITIES,
        annualSpend: usd(61_500),
        grade: "B",
        hasLiveAgreement: true,
        nextRenewal: { id: "ren-orbit", kind: "contract_expiry", window: "Nov 2027" },
        lastNegotiatedLabel: "Oct 2026",
        termsSummary: "−6.1% · Net 60",
        currentDealId: dealId("deal-orbit"),
    },
    {
        id: SUP_DELTA,
        name: "Delta Calibration",
        primaryEmail: "service@deltacal.com",
        categoryId: CAT_LAB,
        annualSpend: usd(44_000),
        grade: "B+",
        hasLiveAgreement: true,
        nextRenewal: { id: "ren-delta", kind: "contract_expiry", window: "Sep 2027" },
        lastNegotiatedLabel: "Sep 2026",
        termsSummary: "−4.4% · Net 45",
        currentDealId: dealId("deal-delta"),
    },
    {
        id: SUP_MERIDIAN,
        name: "Meridian Tapes & Adhesives",
        primaryEmail: "ap@meridiantapes.com",
        categoryId: CAT_PACKAGING,
        annualSpend: usd(15_800),
        hasLiveAgreement: true,
        nextRenewal: { id: "ren-meridian", kind: "auto_renewal_window", window: "Feb 2027" },
        lastNegotiatedLabel: "Never",
        termsSummary: "Price +6% YoY, no review",
        watch: {
            kind: "creeping_auto_renewal",
            detail:
                "Auto-renews in February with a 6% price increase. No one has negotiated this in 3 years. The agent has flagged it as a trigger.",
        },
    },
    {
        id: SUP_PINNACLE,
        name: "Pinnacle Filtration",
        primaryEmail: "team@pinnaclefilter.com",
        categoryId: CAT_MRO,
        annualSpend: usd(22_100),
        hasLiveAgreement: false,
        nextRenewal: null,
        lastNegotiatedLabel: "Never",
        termsSummary: "Spot buys · off-contract",
        watch: {
            kind: "off_contract",
            detail:
                "Off-contract spend with no agreement on file. The agent is monitoring for a consolidation opportunity.",
        },
    },
    {
        id: SUP_SUMMIT,
        name: "Summit Workwear",
        primaryEmail: "ar@summitwork.com",
        categoryId: CAT_PPE,
        annualSpend: usd(19_400),
        hasLiveAgreement: false,
        nextRenewal: null,
        lastNegotiatedLabel: "Never",
        termsSummary: "Spot buys",
    },
    {
        id: SUP_HARBOR,
        name: "Harbor Chemical Supplies",
        primaryEmail: "ap@harborchem.com",
        categoryId: CAT_JANITORIAL,
        annualSpend: usd(33_200),
        hasLiveAgreement: true,
        nextRenewal: { id: "ren-harbor", kind: "contract_expiry", window: "In 26 days", estValue: usd(33_200) },
        lastNegotiatedLabel: "2024",
        termsSummary: "Net 30 · 2024 catalog pricing",
        watch: {
            kind: "expiring",
            detail:
                "Contract expires in 26 days with no renewal in motion. Queued for autonomous renewal outreach.",
        },
    },
    {
        id: SUP_POLARIS,
        name: "Polaris Freight",
        primaryEmail: "contracts@polarisfreight.com",
        categoryId: CAT_LOGISTICS,
        annualSpend: usd(68_400),
        grade: "B",
        hasLiveAgreement: true,
        nextRenewal: { id: "ren-polaris", kind: "contract_expiry", window: "In 18 days", estValue: usd(68_400) },
        lastNegotiatedLabel: "2024",
        termsSummary: "Net 30 · standard freight rates",
    },
    {
        id: SUP_ASPEN,
        name: "Aspen Pack Co.",
        primaryEmail: "sales@aspenpack.com",
        categoryId: CAT_PACKAGING,
        annualSpend: usd(41_600),
        grade: "B-",
        hasLiveAgreement: true,
        nextRenewal: { id: "ren-aspen", kind: "contract_expiry", window: "In 9 days", estValue: usd(41_600) },
        lastNegotiatedLabel: "2024",
        termsSummary: "Net 45 · 2024 annual pricing",
    },
    {
        id: SUP_BEACON,
        name: "Beacon Reagent Supply",
        primaryEmail: "renewals@beaconreagent.com",
        categoryId: CAT_RND,
        annualSpend: usd(54_900),
        grade: "B+",
        hasLiveAgreement: true,
        nextRenewal: { id: "ren-beacon", kind: "contract_expiry", window: "In 5 days", estValue: usd(54_900) },
        lastNegotiatedLabel: "2024",
        termsSummary: "Net 30 · 2024 reagent catalog",
    },
    {
        id: SUP_COASTLINE,
        name: "Coastline Office Supply",
        primaryEmail: "ap@coastlineoffice.com",
        categoryId: CAT_OFFICE,
        annualSpend: usd(27_500),
        grade: "C+",
        hasLiveAgreement: true,
        nextRenewal: { id: "ren-coastline", kind: "auto_renewal_window", window: "In 23 days", estValue: usd(27_500) },
        lastNegotiatedLabel: "2023",
        termsSummary: "Net 30 · 2023 catalog pricing",
        watch: {
            kind: "creeping_auto_renewal",
            detail:
                "Auto-renews in 23 days. Agent will trigger an outreach before the window closes.",
        },
    },
    // ---- In-flight (active) suppliers --------------------------------------
    {
        id: SUP_MAGNOLIA,
        name: "Magnolia Office Furniture",
        primaryEmail: "ar@magnoliaoffice.com",
        categoryId: CAT_OFFICE,
        annualSpend: usd(48_200),
        grade: "B",
        hasLiveAgreement: true,
        nextRenewal: null,
        lastNegotiatedLabel: "Apr 2024",
        termsSummary: "Net 30 · 2024 catalog pricing",
        currentDealId: dealId("deal-magnolia"),
    },
    {
        id: SUP_SENTINEL,
        name: "Sentinel Print Services",
        primaryEmail: "deals@sentinelprint.com",
        categoryId: CAT_OFFICE,
        annualSpend: usd(31_400),
        grade: "B-",
        hasLiveAgreement: true,
        nextRenewal: null,
        lastNegotiatedLabel: "May 2024",
        termsSummary: "Net 30 · 2024 print catalog",
        currentDealId: dealId("deal-sentinel"),
    },
    {
        id: SUP_COBALT,
        name: "Cobalt Tooling",
        primaryEmail: "sales@cobalttools.com",
        categoryId: CAT_MRO,
        annualSpend: usd(72_900),
        grade: "B+",
        hasLiveAgreement: true,
        nextRenewal: null,
        lastNegotiatedLabel: "Jun 2024",
        termsSummary: "Net 30 · 2024 tooling catalog",
        currentDealId: dealId("deal-cobalt"),
    },
    {
        id: SUP_CYPRESS,
        name: "Cypress Janitorial Co.",
        primaryEmail: "contracts@cypressjan.com",
        categoryId: CAT_JANITORIAL,
        annualSpend: usd(54_600),
        grade: "B",
        hasLiveAgreement: true,
        nextRenewal: null,
        lastNegotiatedLabel: "May 2024",
        termsSummary: "Net 45 · 2024 service rates",
        currentDealId: dealId("deal-cypress"),
    },
    {
        id: SUP_NORTHWIND,
        name: "Northwind IT Services",
        primaryEmail: "renewals@northwind-it.com",
        categoryId: CAT_IT,
        annualSpend: usd(89_500),
        grade: "B",
        hasLiveAgreement: true,
        nextRenewal: null,
        lastNegotiatedLabel: "Mar 2024",
        termsSummary: "Net 30 · 2024 catalog",
        currentDealId: dealId("deal-northwind"),
    },
    {
        id: SUP_IRONWOOD,
        name: "Ironwood Pallet & Crate",
        primaryEmail: "ar@ironwoodpc.com",
        categoryId: CAT_PACKAGING,
        annualSpend: usd(38_900),
        grade: "B-",
        hasLiveAgreement: true,
        nextRenewal: null,
        lastNegotiatedLabel: "Jul 2024",
        termsSummary: "Net 30 · 2024 packaging pricing",
        currentDealId: dealId("deal-ironwood"),
    },
    {
        id: SUP_PACIFIC,
        name: "Pacific Freight Brokers",
        primaryEmail: "deals@pacificfreight.com",
        categoryId: CAT_LOGISTICS,
        annualSpend: usd(67_200),
        grade: "B",
        hasLiveAgreement: true,
        nextRenewal: null,
        lastNegotiatedLabel: "Aug 2024",
        termsSummary: "Net 30 · standard lane rates",
        currentDealId: dealId("deal-pacific"),
    },
    {
        id: SUP_QUARTZLINE,
        name: "Quartzline Reagents",
        primaryEmail: "ar@quartzlinerx.com",
        categoryId: CAT_RND,
        annualSpend: usd(58_300),
        grade: "B",
        hasLiveAgreement: true,
        nextRenewal: null,
        lastNegotiatedLabel: "Jun 2024",
        termsSummary: "Net 30 · 2024 reagent catalog",
        currentDealId: dealId("deal-quartzline"),
    },
    {
        id: SUP_VISTA,
        name: "Vista Safety Wear",
        primaryEmail: "sales@vistasafety.com",
        categoryId: CAT_PPE,
        annualSpend: usd(41_700),
        grade: "B-",
        hasLiveAgreement: true,
        nextRenewal: null,
        lastNegotiatedLabel: "May 2024",
        termsSummary: "Net 30 · 2024 PPE pricing",
        currentDealId: dealId("deal-vista"),
    },
    {
        id: SUP_GRANITE,
        name: "Granite Lab Calibration",
        primaryEmail: "service@granitecal.com",
        categoryId: CAT_LAB,
        annualSpend: usd(33_900),
        grade: "B+",
        hasLiveAgreement: true,
        nextRenewal: null,
        lastNegotiatedLabel: "Apr 2024",
        termsSummary: "Net 30 · annual calibration plan",
        currentDealId: dealId("deal-granite"),
    },
];

// ---------------------------------------------------------------------------
// Helpers for the deal records
// ---------------------------------------------------------------------------
function lever(
    name: string,
    description: string,
    currentLabel: string,
    targetLabel: string,
    floorLabel: string,
    currentPct: number,
    targetPct: number,
    floorPct: number,
    kind: Lever["kind"] = "unit_price"
): Lever {
    return { kind, name, description, currentLabel, targetLabel, floorLabel, currentPct, targetPct, floorPct };
}

function audit(actor: AuditEvent["actor"], when: string, event: string, detail: string, idx: number): AuditEvent {
    return { id: `ae-${event}-${idx}`, actor, whenLabel: when, event, detail };
}

// ---------------------------------------------------------------------------
// Scripted deals
// ---------------------------------------------------------------------------

// ---- Crescent (Closed · auto-executed) -----------------------------------
const crescentEnvelope: Lever[] = [
    lever("Unit price", "3.1% above category P50", "Today", "Target −5%", "Floor −2%", 62, 62, 28, "unit_price"),
    lever("Payment terms", "Net 30 vs category Net 60", "Net 30", "Target 60", "Floor 45", 45, 90, 55, "payment_terms"),
    lever("Volume rebate", "None today", "0%", "2% > $85K", "Floor 1.5%", 20, 75, 50, "volume_rebate"),
];

const crescentThread: ThreadItem[] = [
    {
        kind: "email",
        side: "agent",
        from: "Crestwell Partners Procurement",
        whenLabel: "Mon · 9:14",
        subject: "Renewal review — December MRO agreement",
        body: [
            "Hi Marcus, ahead of the December renewal we'd like to align terms for the next 12 months. Reviewing spend and current market pricing, a few items stand out.",
        ],
        terms: [
            ["Unit price (top 10 SKUs)", "Reduce 5%"],
            ["Payment terms", "Move to Net 60"],
            ["Volume rebate", "2% above $85K"],
        ],
        closing: "Happy to commit to the full 12-month renewal in return.",
    },
    {
        kind: "email",
        side: "supplier",
        from: "Marcus Whelan · Crescent",
        whenLabel: "Tue · 16:48",
        subject: "Re: Renewal review",
        body: ["Thanks for the heads up. A few quick reactions:"],
        terms: [
            ["Unit price", "1.5% off list — 5% is too steep"],
            ["Payment terms", "Net 45 if we hold price"],
            ["Volume rebate", "2%, but tier at $90K"],
        ],
        closing: "Let me know how that lands and we can keep going.",
    },
    {
        kind: "email",
        side: "agent",
        from: "Crestwell Partners Procurement",
        whenLabel: "Wed · 8:32",
        subject: "Re: Renewal review",
        body: [
            "Appreciate the flexibility. Net 45 works. On price, benchmarks still sit ~3% below current, so let's meet in the middle rather than 1.5%.",
        ],
        terms: [
            ["Unit price", "3% reduction"],
            ["Payment terms", "Net 45 (Net 60 yr 2)"],
            ["Volume rebate", "2% above $85K"],
        ],
        closing: "Lock these three and we'll commit to the 12-month renewal today.",
    },
    {
        kind: "email",
        side: "supplier",
        from: "Marcus Whelan · Crescent",
        whenLabel: "Fri · 11:21",
        subject: "Re: Renewal review",
        body: [
            "That works. 3%, Net 45 stepping to Net 60 in year two, and the rebate at $85K. Fair given the commitment.",
        ],
        closing: "Pleasure doing business.",
    },
    { kind: "system", variant: "agreement", text: "Agreement detected — all envelope terms converged" },
    {
        kind: "email",
        side: "agent",
        from: "Crestwell Partners Procurement",
        whenLabel: "Fri · 11:24",
        subject: "Confirming agreed terms — please reply to confirm",
        body: ["Great, locking it in. To confirm before we process:"],
        terms: [
            ["Unit price", "−3.0% on top-10 SKUs"],
            ["Payment terms", "Net 45 (Net 60 yr 2)"],
            ["Volume rebate", "2% above $85K annual"],
            ["Auto-renewal", "Replaced with 30-day notice-out"],
            ["Effective", "Dec 14, 2026"],
        ],
        closing: "Reply CONFIRM and we'll issue the updated agreement.",
    },
    {
        kind: "email",
        side: "supplier",
        from: "Marcus Whelan · Crescent",
        whenLabel: "Fri · 11:38",
        subject: "Re: Confirming agreed terms",
        body: ["Confirmed. Send it over."],
    },
    { kind: "system", variant: "ok", text: "Confirmed by supplier — terms locked, agreement recorded" },
    { kind: "system", variant: "ok", text: "Within guardrail · requisition created in SAP Ariba" },
];

const crescentAudit: AuditEvent[] = [
    audit("agent", "Oct 28 · 06:30", "Trigger detected", "Contract renewal window · MRO & Indirect · December agreement", 1),
    audit("agent", "Oct 28 · 06:31", "Re-indexed spend", "18mo AP history (S/4HANA) → $87.4K true annual value", 2),
    audit("agent", "Oct 28 · 06:32", "Classified · autonomous", "Within $150K cap · MRO & Indirect policy applies (target −5%, floor −2%, Net 30→60 latitude) · Tier-B supplier · agent owns the negotiation", 3),
    audit("agent", "Oct 28 · 09:14", "Outreach sent", "Initial email · 3 levers, target asks", 4),
    audit("supplier", "Oct 29 · 16:48", "Supplier response received", "Counter on price, payment terms, rebate", 5),
    audit("agent", "Oct 30 · 08:32", "Round 2 sent", "Concession plan executed within ladder", 6),
    audit("supplier", "Oct 31 · 11:21", "Supplier accepts core terms", "3% / Net 45 / 2% at $85K", 7),
    audit("agent", "Oct 31 · 11:24", "Confirmation requested", "Structured terms recap sent for explicit confirm", 8),
    audit("supplier", "Oct 31 · 11:38", "Supplier confirmed", "\"Confirmed. Send it over.\" — acceptance recorded", 9),
    audit("agent", "Oct 31 · 11:39", "Guardrail check passed", "All levers within ZOPA · under $25K · auto-execute", 10),
    audit("agent", "Oct 31 · 11:42", "Requisition created", "SAP Ariba PR-2026-118204 · amendment under MSA", 11),
    audit("human", "Oct 31 · 11:42", "Buyer notified", "Diogo Passos · for visibility, no action needed", 12),
];

const crescentBenchmark: Benchmark = {
    metric: "Unit price · top 10 MRO SKUs",
    ticks: [
        { label: "P25", value: "−4%", positionPct: 12 },
        { label: "Market P50", value: "par", positionPct: 42 },
        { label: "P75", value: "+7%", positionPct: 82 },
    ],
    supplierPositionPct: 62,
    supplierVsMarket: "+3.1% vs market",
    confidence: "high",
    sources: [
        "Infinity Loop benchmark · 1,240 comparable MRO contracts",
        "Crestwell Partners 18-month AP spend history (S/4HANA)",
        "Public commodity index · abrasives and fasteners",
    ],
    refreshedLabel: "Refreshed 9 days ago",
    rationale:
        "Crescent sits 3.1% above the category median, carries no volume rebate, and pays below-market terms. That defines a $4.3K to $6.1K opportunity. The target was set to market price, Net 60, and a 2% rebate, not an arbitrary percentage off the current price. The walk-away floor stops the agent conceding past −2%.",
};

const crescentOutcome: Outcome = {
    savingsLabel: "$4,287 / yr",
    savingsSub: "first-year · 4.9% of $87,400 · plus $1,200 working-capital NPV",
    metrics: [
        ["Rounds", "3 each side"],
        ["Cycle", "6 business days"],
        ["Human time", "0 min"],
        ["Sentiment", "Positive"],
    ],
    termChanges: [
        { lever: "Unit price (top 10)", before: "List", after: "−3.0%", impact: "−$2,622/yr" },
        { lever: "Payment terms", before: "Net 30", after: "Net 45 (Net 60 yr 2)", impact: "+$1,200 NPV" },
        { lever: "Volume rebate", before: "None", after: "2% above $85K", impact: "−$1,665/yr est" },
        { lever: "Auto-renewal", before: "12-mo roll", after: "30-day notice-out", impact: "Risk ↓" },
    ],
    binding: {
        method: "amendment_under_master",
        title: "Bound as an amendment under the existing MRO Master Agreement",
        description:
            "No new contract required. This renewal amends price and terms under MSA CW-2026-08847, which already carries the governing legal terms. The supplier's confirmation plus the updated PO execute against the master.",
        files: [
            {
                title: "Amendment_v1_signed.pdf",
                detail: "DocuSign CFE-44218 · Crescent (Whelan) + Crestwell Partners agent (delegation #DEL-2026-MRO-2)",
            },
            {
                title: "Negotiation_transcript.pdf",
                detail: "Full email thread + agent reasoning log · 14 KB · attached for audit",
            },
        ],
    },
};

// ---- Apex (Paused · needs decision) --------------------------------------
const apexEnvelope: Lever[] = [
    lever("Unit price", "Supplier asking +4.0% · ceiling +2.0%", "+4% ask", "Hold flat", "Floor +2%", 88, 35, 60, "unit_price"),
    lever("Payment terms", "Net 30 · room to extend", "Net 30", "Target 60", "Floor 45", 45, 85, 55, "payment_terms"),
];

const apexThread: ThreadItem[] = [
    {
        kind: "email",
        side: "agent",
        from: "Crestwell Partners Procurement",
        whenLabel: "Mon · 10:02",
        subject: "Annual renewal — reagent supply",
        body: ["Hi, ahead of renewal we'd like to hold pricing flat for the coming year and align payment terms to Net 60."],
        terms: [
            ["Unit price", "Hold flat (no increase)"],
            ["Payment terms", "Net 60"],
        ],
        closing: "Can we get these confirmed before the renewal date?",
    },
    {
        kind: "email",
        side: "supplier",
        from: "Apex Lab Reagents",
        whenLabel: "Wed · 13:30",
        subject: "Re: Annual renewal",
        body: [
            "Thanks. Reagent inputs are up sharply this year. The best we can do is a 4% increase, and we'd prefer to keep Net 30.",
        ],
        terms: [
            ["Unit price", "+4.0% increase"],
            ["Payment terms", "Net 30 preferred"],
        ],
        closing: "Happy to discuss, but 4% is firm on our side for now.",
    },
    {
        kind: "system",
        variant: "warn",
        text: "Supplier offer (+4.0%) exceeds envelope ceiling (+2.0%) — agent paused, escalated to you",
    },
];

const apexAudit: AuditEvent[] = [
    audit("agent", "Nov 02 · 08:12", "Trigger detected", "Contract expiry in 21 days · R&D Consumables · annual renewal", 1),
    audit("agent", "Nov 02 · 08:12", "Re-indexed spend", "18mo AP history (S/4HANA) → $92.3K true annual value", 2),
    audit("agent", "Nov 02 · 08:13", "Classified · autonomous", "Within $150K cap · R&D Consumables policy applies (ceiling +2%, floor +4%, Net 30→60 latitude) · Tier-2 supplier · agent owns the negotiation", 3),
    audit("agent", "Nov 02 · 10:02", "Outreach sent", "Asked flat price, Net 60", 4),
    audit("supplier", "Nov 04 · 13:30", "Supplier response received", "+4.0% increase, Net 30 preferred", 5),
    audit("agent", "Nov 04 · 13:31", "Floor breach detected", "Counter exceeds +2.0% ceiling for R&D Consumables", 6),
    audit("human", "Nov 04 · 13:31", "Escalated to human", "Routed to Diogo Passos · negotiation paused pending decision", 7),
];

const apexDecision: DecisionPrompt = {
    title: "The agent hit your walk-away floor",
    detail:
        "Apex will not hold price flat. Their best offer is a +4.0% increase; the envelope ceiling is +2.0%. The agent will not go past a floor without you. How do you want to proceed?",
    options: [
        {
            id: "apex-adjust",
            kind: "adjust",
            title: "Raise the ceiling to +4% (this deal only)",
            subtitle: "Reagent index is genuinely up 6% YoY — one-time exception, agent closes",
            recommended: true,
        },
        {
            id: "apex-trade",
            kind: "trade",
            title: "Hold firm, authorize a term trade instead",
            subtitle: "Let the agent offer Net 30→45 to claw back the increase in working capital",
        },
        {
            id: "apex-take",
            kind: "take",
            title: "Take over manually",
            subtitle: "Open the thread and negotiate this one yourself",
        },
        {
            id: "apex-walk",
            kind: "walk",
            title: "Walk away · re-source",
            subtitle: "Decline renewal, send to Tactical Sourcing for competitive quotes",
        },
    ],
};

const apexEscalation: Escalation = {
    reason: "floor_breach",
    severity: "red",
    title: "Apex Lab Reagents · counter is outside the envelope",
    description:
        "Supplier countered at +4.0% on renewal. Your ceiling for this category is +2.0%. The agent stopped rather than concede past the floor.",
    decision: apexDecision,
};

const apexBenchmark: Benchmark = {
    metric: "Reagent index · renewal",
    ticks: [
        { label: "P25", value: "−3%", positionPct: 16 },
        { label: "Market P50", value: "par", positionPct: 44 },
        { label: "P75", value: "+3%", positionPct: 78 },
    ],
    supplierPositionPct: 88,
    supplierVsMarket: "+4% ask vs market",
    confidence: "medium",
    sources: [
        "Infinity Loop benchmark · 310 reagent contracts",
        "Published reagent input index (up ~6% YoY)",
        "Crestwell Partners spend history",
    ],
    refreshedLabel: "Refreshed 6 days ago",
    rationale:
        "The supplier's +4% ask sits above P75 for the category. The reagent input index is genuinely up about 6% year over year, so the increase is partly defensible, which is why the agent escalated rather than walked. This is a judgement call on whether to accept a real, market-driven increase or re-source. Confidence is Medium because reagent benchmarks are thinner than MRO.",
};

// ---- Brightway (Held · above cap) ----------------------------------------
const brightwayThread: ThreadItem[] = [
    {
        kind: "system",
        variant: "warn",
        text: "Annual value $310K exceeds $150K autonomous cap — held at triage, no outreach sent",
    },
];

const brightwayAudit: AuditEvent[] = [
    audit("agent", "Nov 03 · 07:40", "Trigger detected", "Contract expiry in 58 days · Freight & Logistics", 1),
    audit("agent", "Nov 03 · 07:41", "Re-indexed spend", "18mo AP history → $310K true annual value", 2),
    audit("agent", "Nov 03 · 07:41", "Threshold breach", "$310K > $150K autonomous cap", 3),
    audit("human", "Nov 03 · 07:41", "Escalated to human", "Held for reassignment · no supplier contact made", 4),
];

const brightwayDecision: DecisionPrompt = {
    title: "This deal outgrew the tail",
    detail:
        "On re-indexing 18 months of AP data, Brightway's true annual value is $310K, above your $150K autonomous cap. The agent held it rather than auto-negotiate a strategic-sized contract.",
    options: [
        {
            id: "bw-reassign",
            kind: "reassign",
            title: "Reassign to the strategic team + generate full playbook",
            subtitle: "Hands off to a buyer with the complete benchmark playbook attached",
            recommended: true,
        },
        {
            id: "bw-keep",
            kind: "keep",
            title: "Keep in tail (override the cap)",
            subtitle: "Authorize autonomous handling despite the value",
        },
        {
            id: "bw-take",
            kind: "take",
            title: "Take over manually",
            subtitle: "You run it yourself from here",
        },
    ],
};

const brightwayEscalation: Escalation = {
    reason: "above_autonomous_cap",
    severity: "amber",
    title: "Brightway Logistics · larger than tail spend",
    description:
        "Re-indexed annual value came back at $310K, above your $150K autonomous cap. This should run as a strategic deal, not autonomous tail.",
    decision: brightwayDecision,
};

// ---- Orion (Stalled · supplier silent) -----------------------------------
const orionEnvelope: Lever[] = [
    lever("Unit price", "4.4% above benchmark", "Today", "Target −5%", "Floor −2%", 64, 30, 55),
];

const orionThread: ThreadItem[] = [
    {
        kind: "email",
        side: "agent",
        from: "Crestwell Partners Procurement",
        whenLabel: "Oct 27 · 9:00",
        subject: "Renewal — office & print supply",
        body: ["Hi, we'd like to review pricing ahead of your renewal. Could you share current rates so we can align?"],
        closing: "Looking forward to it.",
    },
    {
        kind: "system",
        variant: "warn",
        text: "No response · 2 automated follow-ups sent (Day 3, Day 6) · agent paused after 7 days",
    },
];

const orionAudit: AuditEvent[] = [
    audit("agent", "Oct 27 · 06:30", "Trigger detected", "Contract expiry in 12 days · Office Supplies · annual renewal", 1),
    audit("agent", "Oct 27 · 06:31", "Re-indexed spend", "18mo AP history (S/4HANA) → $38.9K true annual value", 2),
    audit("agent", "Oct 27 · 06:32", "Classified · autonomous", "Within $150K cap · Office Supplies policy applies · Tier-C+ supplier · agent owns the negotiation", 3),
    audit("agent", "Oct 27 · 09:00", "Outreach sent", "Opening email · requested current rates", 4),
    audit("agent", "Oct 30 · 09:00", "Follow-up 1 sent", "Automated nudge", 5),
    audit("agent", "Nov 02 · 09:00", "Follow-up 2 sent", "Automated nudge", 6),
    audit("human", "Nov 03 · 09:00", "Escalated to human", "No response in 7 days · paused for your decision", 7),
];

const orionDecision: DecisionPrompt = {
    title: "Supplier has gone quiet",
    detail:
        "Orion hasn't responded to the opening outreach or two follow-ups across 7 days. The agent paused rather than nag indefinitely.",
    options: [
        {
            id: "orion-trade",
            kind: "trade",
            title: "Send a final notice with a deadline",
            subtitle: "One more email: confirm by Friday or we re-source",
            recommended: true,
        },
        {
            id: "orion-walk",
            kind: "walk",
            title: "Let it renew as-is and re-source next cycle",
            subtitle: "Low value · accept current terms for now",
        },
        {
            id: "orion-take",
            kind: "take",
            title: "Switch channel · call the rep",
            subtitle: "Take it off email and reach out by phone",
        },
    ],
};

const orionEscalation: Escalation = {
    reason: "supplier_silent",
    severity: "amber",
    title: "Orion Office & Print · supplier non-responsive",
    description: "No reply after the opening email and two automated nudges over 7 days. The agent won't keep chasing without your call.",
    decision: orionDecision,
};

// ---- Vertex (Closed · net-new PO) ----------------------------------------
const vertexEnvelope: Lever[] = [
    lever("Unit price", "Net-new · vs opening quote", "Quote", "Target −6%", "Floor −2%", 55, 55, 25),
    lever("Payment terms", "Supplier standard Net 30", "Net 30", "Target 60", "Floor 45", 45, 88, 55, "payment_terms"),
];

const vertexThread: ThreadItem[] = [
    {
        kind: "email",
        side: "agent",
        from: "Crestwell Partners Procurement",
        whenLabel: "Mon · 10:00",
        subject: "Sourcing — PPE basket, annual supply",
        body: [
            "Hi, Crestwell Partners is sourcing PPE for the coming year. We'd like a quote on the attached 12-SKU basket, with Net 60 terms against an annual commitment.",
        ],
        terms: [
            ["Basket", "12 SKUs · ~$57K annual"],
            ["Payment terms", "Net 60"],
        ],
        closing: "Happy to commit for 12 months at the right number.",
    },
    {
        kind: "email",
        side: "supplier",
        from: "Vertex Safety Equip.",
        whenLabel: "Tue · 14:10",
        subject: "Re: Sourcing — PPE basket",
        body: [
            "Thanks for the opportunity. Pricing is attached. We typically work Net 30, but can look at terms for committed volume.",
        ],
        terms: [
            ["Unit price", "Opening quote"],
            ["Payment terms", "Net 30 standard"],
        ],
        closing: "Let us know.",
    },
    {
        kind: "email",
        side: "agent",
        from: "Crestwell Partners Procurement",
        whenLabel: "Wed · 9:05",
        subject: "Re: Sourcing — PPE basket",
        body: [
            "Appreciate it. Benchmarked against market for this basket, we'd target ~6% below your opening quote, with Net 60 given the annual commitment.",
        ],
        terms: [
            ["Unit price", "−6% vs quote"],
            ["Payment terms", "Net 60"],
        ],
        closing: "Land close to that and we'll commit to the full basket.",
    },
    {
        kind: "email",
        side: "supplier",
        from: "Vertex Safety Equip.",
        whenLabel: "Thu · 16:20",
        subject: "Re: Sourcing — PPE basket",
        body: [
            "We can do 5.5% off the quote and Net 60 if you commit to the full basket for 12 months. That works for us.",
        ],
        closing: "Ready when you are.",
    },
    { kind: "system", variant: "agreement", text: "Agreement detected — terms converged" },
    {
        kind: "email",
        side: "agent",
        from: "Crestwell Partners Procurement",
        whenLabel: "Thu · 16:22",
        subject: "Confirming before we raise the PO — please reply to confirm",
        body: ["Great. Confirming the terms before we issue the purchase order:"],
        terms: [
            ["Unit price", "−5.5% vs quote"],
            ["Payment terms", "Net 60"],
            ["Term", "12-month basket"],
            ["Governed by", "Crestwell Partners Standard Purchasing T&Cs v6"],
        ],
        closing: "Reply CONFIRM and we'll issue the PO.",
    },
    {
        kind: "email",
        side: "supplier",
        from: "Vertex Safety Equip.",
        whenLabel: "Thu · 16:35",
        subject: "Re: Confirming before we raise the PO",
        body: ["Confirmed."],
    },
    { kind: "system", variant: "ok", text: "Confirmed by supplier — terms locked, acceptance recorded" },
    { kind: "system", variant: "ok", text: "Net-new · PO-4500219887 issued under standard purchasing T&Cs" },
];

const vertexAudit: AuditEvent[] = [
    audit("agent", "Oct 28 · 07:00", "Trigger detected", "Net-new sourcing request · PPE · 12-SKU basket", 1),
    audit("agent", "Oct 28 · 07:01", "Spend modeled", "Benchmark + Crestwell Partners historicals → estimated $56.8K annual value", 2),
    audit("agent", "Oct 28 · 07:02", "Classified · autonomous", "Within $150K cap · PPE policy applies · net-new supplier, no MSA · agent runs the sourcing under standard purchasing T&Cs", 3),
    audit("agent", "Oct 28 · 10:00", "Outreach sent", "Net-new sourcing · 12-SKU basket, Net 60 ask", 4),
    audit("supplier", "Oct 29 · 14:10", "Quote received", "Opening pricing, Net 30 standard", 5),
    audit("agent", "Oct 30 · 09:05", "Round 2 sent", "Target −6% vs quote, Net 60", 6),
    audit("supplier", "Oct 31 · 16:20", "Supplier accepts", "−5.5%, Net 60, 12-month commitment", 7),
    audit("agent", "Oct 31 · 16:22", "Confirmation requested", "Structured recap incl. governing T&Cs", 8),
    audit("supplier", "Oct 31 · 16:35", "Supplier confirmed", "\"Confirmed.\" — acceptance recorded", 9),
    audit("agent", "Oct 31 · 16:36", "Guardrail check passed", "In-envelope · under $150K · auto-execute", 10),
    audit("agent", "Oct 31 · 16:38", "PO issued", "SAP Ariba PO-4500219887 · standard T&Cs v6 by reference", 11),
    audit("human", "Oct 31 · 16:38", "Buyer notified", "Diogo Passos · visibility only", 12),
];

const vertexBenchmark: Benchmark = {
    metric: "PPE basket · 12 SKUs",
    ticks: [
        { label: "P25", value: "−6%", positionPct: 14 },
        { label: "Market P50", value: "par", positionPct: 46 },
        { label: "P75", value: "+9%", positionPct: 84 },
    ],
    supplierPositionPct: 55,
    supplierVsMarket: "opening quote ≈ +5% vs market",
    confidence: "high",
    sources: [
        "Infinity Loop benchmark · 480 PPE quotes",
        "Crestwell Partners prior-year PPE spend",
        "Manufacturer list-price feeds",
    ],
    refreshedLabel: "Refreshed 4 days ago",
    rationale:
        "Net-new supplier, so there is no incumbent price. The opening quote benchmarked about 5% above market for this basket, which set the −5.5% target. Net 60 is market-standard for this category at this volume.",
};

const vertexOutcome: Outcome = {
    savingsLabel: "$3,110 / yr",
    savingsSub: "first-year · 5.5% vs opening quote · net-new supplier",
    metrics: [
        ["Rounds", "2 each side"],
        ["Cycle", "4 business days"],
        ["Human time", "0 min"],
        ["Sentiment", "Positive"],
    ],
    termChanges: [
        { lever: "Unit price", before: "Opening quote", after: "−5.5%", impact: "−$3,110/yr" },
        { lever: "Payment terms", before: "Net 30 (std)", after: "Net 60", impact: "+$640 NPV" },
        { lever: "Contract form", before: "None", after: "PO + std T&Cs", impact: "New" },
    ],
    binding: {
        method: "po_is_contract",
        title: "Bound as a purchase order under Crestwell Partners's standard purchasing T&Cs",
        description:
            "No master agreement existed, so the PO is the contract. It incorporates Crestwell Partners Standard Purchasing T&Cs (v6) by reference, and Vertex accepts by acknowledging the PO. The negotiated terms populate the PO directly. At this value, no separately signed contract is required.",
        files: [
            {
                title: "PO-4500219887.pdf",
                detail: "SAP Ariba · incorporates Crestwell Partners Standard Purchasing T&Cs v6 by reference",
            },
            {
                title: "Supplier_PO_acknowledgement.pdf",
                detail: "Vertex acknowledgement · acceptance of PO terms · time-stamped",
            },
            {
                title: "Negotiation_transcript.pdf",
                detail: "Full email thread + agent reasoning log · attached for audit",
            },
        ],
    },
};

// ---- Cardinal (Negotiating · awaiting supplier) --------------------------
const cardinalEnvelope: Lever[] = [
    lever("Unit price", "5.2% above benchmark", "Today", "Target −5%", "Floor −2%", 62, 30, 55),
    lever("Payment terms", "Net 30 today", "Net 30", "Target 60", "Floor 45", 45, 88, 55, "payment_terms"),
];
const cardinalThread: ThreadItem[] = [
    {
        kind: "email",
        side: "agent",
        from: "Crestwell Partners Procurement",
        whenLabel: "Mon · 10:10",
        subject: "Packaging renewal review",
        body: ["Hi, reviewing the pallet and crate agreement ahead of renewal. We would like to align price to market and extend terms."],
        terms: [
            ["Unit price", "Reduce 5%"],
            ["Payment terms", "Net 60"],
        ],
        closing: "Happy to commit for 12 months.",
    },
    {
        kind: "email",
        side: "supplier",
        from: "Cardinal Pallet & Crate",
        whenLabel: "Tue · 15:00",
        subject: "Re: Packaging renewal review",
        body: ["Lumber is volatile right now. We can look at 2-3% and hold terms."],
        terms: [
            ["Unit price", "2–3% off"],
            ["Payment terms", "Net 30 preferred"],
        ],
        closing: "Let us keep talking.",
    },
    { kind: "system", variant: "ok", text: "Round 2 sent · within concession ladder · awaiting supplier" },
];
const cardinalAudit: AuditEvent[] = [
    audit("agent", "Nov 01 · 07:00", "Trigger detected", "Contract expiry in 34 days · Packaging · annual renewal", 1),
    audit("agent", "Nov 01 · 07:01", "Re-indexed spend", "18mo AP history (S/4HANA) → $74.2K true annual value", 2),
    audit("agent", "Nov 01 · 07:02", "Classified · autonomous", "Within $150K cap · Packaging policy applies · Tier-B− supplier · agent owns the negotiation", 3),
    audit("agent", "Nov 01 · 10:10", "Outreach sent", "Opening asks", 4),
    audit("supplier", "Nov 02 · 15:00", "Supplier counter", "Partial agreement", 5),
    audit("agent", "Nov 03 · 09:20", "Round 2 sent", "Within ladder", 6),
];

// ---- Orbit (Closed · auto) -----------------------------------------------
const orbitEnvelope: Lever[] = [lever("Unit price", "6.1% above benchmark", "Today", "Target −6%", "Floor −2%", 68, 30, 55)];
const orbitThread: ThreadItem[] = [
    {
        kind: "email",
        side: "agent",
        from: "Crestwell Partners Procurement",
        whenLabel: "Oct 12 · 9:30",
        subject: "Facilities supply renewal",
        body: ["Hi, ahead of renewal we would like to align pricing to market and move to Net 60."],
        terms: [
            ["Unit price", "Reduce 6%"],
            ["Payment terms", "Net 60"],
        ],
        closing: "Open to a multi-year commit.",
    },
    {
        kind: "email",
        side: "supplier",
        from: "Orbit Janitorial",
        whenLabel: "Oct 14 · 11:00",
        subject: "Re: Facilities supply renewal",
        body: ["We can do 6.1% off and Net 60 on a 12-month renewal."],
        closing: "Works for us.",
    },
    { kind: "system", variant: "agreement", text: "Agreement detected — terms converged" },
    {
        kind: "email",
        side: "agent",
        from: "Crestwell Partners Procurement",
        whenLabel: "Oct 14 · 11:20",
        subject: "Confirming agreed terms — please reply to confirm",
        body: ["Confirming:"],
        terms: [
            ["Unit price", "−6.1%"],
            ["Payment terms", "Net 60"],
            ["Effective", "Nov 1"],
        ],
        closing: "Reply CONFIRM to proceed.",
    },
    {
        kind: "email",
        side: "supplier",
        from: "Orbit Janitorial",
        whenLabel: "Oct 14 · 13:05",
        subject: "Re: Confirming agreed terms",
        body: ["Confirmed."],
    },
    { kind: "system", variant: "ok", text: "Confirmed — terms locked, requisition created" },
];
const orbitAudit: AuditEvent[] = [
    audit("agent", "Oct 12 · 07:00", "Trigger detected", "Price drift detected · Janitorial · 11mo since last review", 1),
    audit("agent", "Oct 12 · 07:01", "Re-indexed spend", "18mo AP history (S/4HANA) → $61.5K true annual value", 2),
    audit("agent", "Oct 12 · 07:02", "Classified · autonomous", "Within $150K cap · Janitorial policy applies · Tier-B supplier · agent owns the negotiation", 3),
    audit("agent", "Oct 12 · 09:30", "Outreach sent", "2 levers", 4),
    audit("supplier", "Oct 14 · 11:00", "Supplier accepts", "−6.1%, Net 60", 5),
    audit("agent", "Oct 14 · 11:20", "Confirmation requested", "Structured recap", 6),
    audit("supplier", "Oct 14 · 13:05", "Supplier confirmed", "Acceptance recorded", 7),
    audit("agent", "Oct 14 · 13:07", "Requisition created", "Ariba · amendment under MSA", 8),
];
const orbitOutcome: Outcome = {
    savingsLabel: "$3,750 / yr",
    savingsSub: "first-year · 6.1% of $61,500",
    metrics: [
        ["Rounds", "1 each side"],
        ["Cycle", "2 days"],
        ["Human time", "0 min"],
        ["Sentiment", "Positive"],
    ],
    termChanges: [
        { lever: "Unit price", before: "List", after: "−6.1%", impact: "−$3,750/yr" },
        { lever: "Payment terms", before: "Net 30", after: "Net 60", impact: "+$540 NPV" },
    ],
    binding: {
        method: "amendment_under_master",
        title: "Bound as an amendment under the existing facilities MSA",
        description: "Renewal under an existing master. The confirmation and updated PO execute against the MSA. No new contract.",
        files: [
            { title: "Amendment_signed.pdf", detail: "DocuSign · Orbit + Crestwell Partners agent under delegation" },
            { title: "Negotiation_transcript.pdf", detail: "Full thread + reasoning · attached for audit" },
        ],
    },
};

// ---- Delta (Closed · auto) -----------------------------------------------
const deltaEnvelope: Lever[] = [lever("Service rate", "4.4% above benchmark", "Today", "Target −4%", "Floor −2%", 60, 32, 55, "service_rate")];
const deltaThread: ThreadItem[] = [
    {
        kind: "email",
        side: "agent",
        from: "Crestwell Partners Procurement",
        whenLabel: "Sep 20 · 10:00",
        subject: "Calibration services renewal",
        body: ["Aligning rates to market for the coming year, targeting Net 60."],
        terms: [
            ["Rate", "Reduce 4%"],
            ["Payment terms", "Net 60"],
        ],
        closing: "Thanks.",
    },
    {
        kind: "email",
        side: "supplier",
        from: "Delta Calibration",
        whenLabel: "Sep 22 · 9:15",
        subject: "Re: Calibration services renewal",
        body: ["We can do 4.4% and Net 45."],
        closing: "Best we can offer on terms.",
    },
    { kind: "system", variant: "agreement", text: "Agreement detected — terms converged" },
    {
        kind: "email",
        side: "agent",
        from: "Crestwell Partners Procurement",
        whenLabel: "Sep 22 · 9:40",
        subject: "Confirming agreed terms — please reply to confirm",
        body: ["Confirming:"],
        terms: [
            ["Rate", "−4.4%"],
            ["Payment terms", "Net 45"],
        ],
        closing: "Reply CONFIRM to proceed.",
    },
    {
        kind: "email",
        side: "supplier",
        from: "Delta Calibration",
        whenLabel: "Sep 22 · 14:00",
        subject: "Re: Confirming agreed terms",
        body: ["Confirmed."],
    },
    { kind: "system", variant: "ok", text: "Confirmed — terms locked, requisition created" },
];
const deltaAudit: AuditEvent[] = [
    audit("agent", "Sep 20 · 07:00", "Trigger detected", "Contract renewal window · Lab Services · annual renewal", 1),
    audit("agent", "Sep 20 · 07:01", "Re-indexed spend", "18mo AP history (S/4HANA) → $44.0K true annual value", 2),
    audit("agent", "Sep 20 · 07:02", "Classified · autonomous", "Within $150K cap · Lab Services policy applies · Tier-B+ supplier · agent owns the negotiation", 3),
    audit("agent", "Sep 20 · 10:00", "Outreach sent", "", 4),
    audit("supplier", "Sep 22 · 09:15", "Supplier accepts", "−4.4%, Net 45", 5),
    audit("agent", "Sep 22 · 09:40", "Confirmation requested", "", 6),
    audit("supplier", "Sep 22 · 14:00", "Supplier confirmed", "", 7),
    audit("agent", "Sep 22 · 14:02", "Requisition created", "Ariba", 8),
];
const deltaOutcome: Outcome = {
    savingsLabel: "$1,940 / yr",
    savingsSub: "first-year · 4.4% of $44,000",
    metrics: [
        ["Rounds", "1 each side"],
        ["Cycle", "2 days"],
        ["Human time", "0 min"],
        ["Sentiment", "Positive"],
    ],
    termChanges: [
        { lever: "Service rate", before: "List", after: "−4.4%", impact: "−$1,940/yr" },
        { lever: "Payment terms", before: "Net 30", after: "Net 45", impact: "+$210 NPV" },
    ],
    binding: {
        method: "po_is_contract",
        title: "Bound as a PO under standard purchasing T&Cs",
        description: "Net-new service order. The PO incorporates Crestwell Partners standard T&Cs by reference; the supplier accepts on acknowledgement.",
        files: [
            { title: "PO-4500219420.pdf", detail: "SAP Ariba · standard T&Cs v6 by reference" },
            { title: "Negotiation_transcript.pdf", detail: "Full thread + reasoning · attached" },
        ],
    },
};

// ---- Nimbus (Closed · e-signed order form) -------------------------------
const nimbusEnvelope: Lever[] = [lever("Unit price", "5.3% above benchmark", "Today", "Target −5%", "Floor −2%", 64, 30, 55)];
const nimbusThread: ThreadItem[] = [
    {
        kind: "email",
        side: "agent",
        from: "Crestwell Partners Procurement",
        whenLabel: "Sep 2 · 9:00",
        subject: "IT hardware framework renewal",
        body: ["Aligning the hardware framework to market, targeting 5% and Net 60."],
        terms: [
            ["Unit price", "Reduce 5%"],
            ["Payment terms", "Net 60"],
        ],
        closing: "Open to annual commit.",
    },
    {
        kind: "email",
        side: "supplier",
        from: "Nimbus IT Reseller",
        whenLabel: "Sep 4 · 14:00",
        subject: "Re: IT hardware framework renewal",
        body: ["5.3% works, Net 45 on terms."],
        closing: "Can confirm with an order form.",
    },
    { kind: "system", variant: "agreement", text: "Agreement detected — terms converged" },
    {
        kind: "email",
        side: "agent",
        from: "Crestwell Partners Procurement",
        whenLabel: "Sep 4 · 14:20",
        subject: "Confirming agreed terms — please reply to confirm",
        body: ["Confirming:"],
        terms: [
            ["Unit price", "−5.3%"],
            ["Payment terms", "Net 45"],
            ["Value band", "$100K–$150K · e-sign"],
        ],
        closing: "Reply CONFIRM; the order form will route for signature.",
    },
    {
        kind: "email",
        side: "supplier",
        from: "Nimbus IT Reseller",
        whenLabel: "Sep 4 · 16:30",
        subject: "Re: Confirming agreed terms",
        body: ["Confirmed."],
    },
    { kind: "system", variant: "ok", text: "Confirmed — order form routed for e-signature" },
    { kind: "system", variant: "ok", text: "Countersigned by buyer · requisition created" },
];
const nimbusAudit: AuditEvent[] = [
    audit("agent", "Sep 02 · 07:00", "Trigger detected", "Contract renewal window · IT Hardware · annual renewal", 1),
    audit("agent", "Sep 02 · 07:01", "Re-indexed spend", "18mo AP history (S/4HANA) → $128.0K true annual value", 2),
    audit("agent", "Sep 02 · 07:02", "Classified · autonomous, buyer e-sign", "Within $150K cap, in $100K–$150K e-sign band · IT Hardware policy applies · Tier-B supplier · agent negotiates, you countersign", 3),
    audit("agent", "Sep 02 · 09:00", "Outreach sent", "", 4),
    audit("supplier", "Sep 04 · 14:00", "Supplier accepts", "−5.3%, Net 45", 5),
    audit("agent", "Sep 04 · 14:20", "Confirmation requested", "", 6),
    audit("supplier", "Sep 04 · 16:30", "Supplier confirmed", "", 7),
    audit("agent", "Sep 04 · 16:32", "E-sign required", "$128K in $100K–$150K band", 8),
    audit("human", "Sep 05 · 10:15", "Buyer countersigned", "Diogo Passos · DocuSign", 9),
    audit("agent", "Sep 05 · 10:16", "Requisition created", "Ariba", 10),
];
const nimbusOutcome: Outcome = {
    savingsLabel: "$6,820 / yr",
    savingsSub: "first-year · 5.3% of $128,000",
    metrics: [
        ["Rounds", "2 each side"],
        ["Cycle", "3 days"],
        ["Human time", "4 min (e-sign)"],
        ["Sentiment", "Positive"],
    ],
    termChanges: [
        { lever: "Unit price", before: "List", after: "−5.3%", impact: "−$6,820/yr" },
        { lever: "Payment terms", before: "Net 30", after: "Net 45", impact: "+$1,050 NPV" },
    ],
    binding: {
        method: "esigned_order_form",
        title: "Bound as an e-signed order form (value band)",
        description: "At $128K this fell in the $100K–$150K band, so the agreed terms were rendered as an order form and countersigned by the buyer before the requisition.",
        files: [
            { title: "OrderForm_signed.pdf", detail: "DocuSign · Nimbus + Diogo Passos (buyer)" },
            { title: "Negotiation_transcript.pdf", detail: "Full thread + reasoning · attached" },
        ],
    },
};

// ---------------------------------------------------------------------------
// Assemble the deals
// ---------------------------------------------------------------------------
const greenNote = (title: string, detail: string): OverviewNote => ({ tone: "green", title, detail });
const amberNote = (title: string, detail: string): OverviewNote => ({ tone: "amber", title, detail });

function buildDeal(args: {
    id: string;
    supplier: Supplier;
    state: DealState;
    statValuePairs: Array<[string, string]>;
    progressPct: number;
    opportunityRange?: { low: number; high: number };
    envelope: Lever[];
    benchmark?: Benchmark;
    thread: ThreadItem[];
    auditTrail: AuditEvent[];
    overviewNote: OverviewNote;
    escalation?: Escalation;
    outcome?: Outcome;
}): Deal {
    const cat = categories.find((c) => c.id === args.supplier.categoryId)!;
    const base: Deal = {
        id: dealId(args.id),
        supplierId: args.supplier.id,
        supplierName: args.supplier.name,
        supplierEmail: args.supplier.primaryEmail,
        categoryId: args.supplier.categoryId,
        categoryName: cat.name,
        annualValue: args.supplier.annualSpend,
        grade: args.supplier.grade,
        progressPct: args.progressPct,
        statValuePairs: args.statValuePairs,
        opportunityRange: args.opportunityRange
            ? { low: usd(args.opportunityRange.low), high: usd(args.opportunityRange.high) }
            : undefined,
        envelope: args.envelope,
        benchmark: args.benchmark,
        thread: args.thread,
        auditTrail: args.auditTrail,
        overviewNote: args.overviewNote,
        state: args.state,
        escalation: args.escalation,
        outcome: args.outcome,
        statusChip: { label: "", tone: "slate" },
        resultLabel: "",
    };
    base.statusChip = dealBadge(args.state, base);
    base.resultLabel = dealResultLabel(args.state, base);
    return base;
}

// Historical closed deals — give a few suppliers >1 negotiation on record.
interface PastSpec {
    id: string;
    supplierId: SupplierId;
    closedDate: string;          // e.g. "Dec 12, 2024"
    annualValue: number;          // value at that time (in $)
    savingsLabel: string;         // "$3,940 / yr"
    savingsSub: string;
    rounds: number;
}

function buildPastDeals(): Deal[] {
    const specs: PastSpec[] = [
        { id: "deal-crescent-2024",   supplierId: SUP_CRESCENT, closedDate: "Dec 12, 2024", annualValue: 79_800,  savingsLabel: "$2,610 / yr",  savingsSub: "3.3% of $79,800 · first year",  rounds: 2 },
        { id: "deal-crescent-2025",   supplierId: SUP_CRESCENT, closedDate: "Dec 14, 2025", annualValue: 83_200,  savingsLabel: "$3,940 / yr",  savingsSub: "4.7% of $83,200 · first year",  rounds: 3 },
        { id: "deal-vertex-2025",     supplierId: SUP_VERTEX,   closedDate: "Aug 22, 2025", annualValue: 51_200,  savingsLabel: "$2,150 / yr",  savingsSub: "4.2% of $51,200 · first year",  rounds: 2 },
        { id: "deal-orbit-2025-mar",  supplierId: SUP_ORBIT,    closedDate: "Mar 04, 2025", annualValue: 57_400,  savingsLabel: "$2,100 / yr",  savingsSub: "3.7% of $57,400 · first year",  rounds: 2 },
        { id: "deal-orbit-2025-sep",  supplierId: SUP_ORBIT,    closedDate: "Sep 18, 2025", annualValue: 60_100,  savingsLabel: "$2,640 / yr",  savingsSub: "4.4% of $60,100 · auto-renewal", rounds: 1 },
        { id: "deal-nimbus-2025",     supplierId: SUP_NIMBUS,   closedDate: "Mar 28, 2025", annualValue: 121_000, savingsLabel: "$4,840 / yr",  savingsSub: "4.0% of $121,000 · first year", rounds: 3 },
        { id: "deal-delta-2025",      supplierId: SUP_DELTA,    closedDate: "Mar 12, 2025", annualValue: 40_500,  savingsLabel: "$1,510 / yr",  savingsSub: "3.7% of $40,500 · annual",      rounds: 1 },
        { id: "deal-orion-2024",      supplierId: SUP_ORION,    closedDate: "Nov 18, 2024", annualValue: 35_200,  savingsLabel: "$1,090 / yr",  savingsSub: "3.1% of $35,200 · auto-renewal", rounds: 2 },
    ];
    return specs.map((spec) => {
        const sup = suppliers.find((s) => s.id === spec.supplierId)!;
        const cat = categories.find((c) => c.id === sup.categoryId)!;
        const audit: AuditEvent[] = [
            { id: `ae-${spec.id}-1`, actor: "agent",    whenLabel: `${spec.closedDate} · 07:00`, event: "Trigger detected",      detail: `Annual renewal · ${cat.name}` },
            { id: `ae-${spec.id}-2`, actor: "agent",    whenLabel: `${spec.closedDate} · 07:02`, event: "Classified · autonomous", detail: `Within $150K cap · ${cat.name} policy applies` },
            { id: `ae-${spec.id}-3`, actor: "agent",    whenLabel: `${spec.closedDate} · 09:30`, event: "Outreach sent",         detail: "Opening asks · within envelope" },
            { id: `ae-${spec.id}-4`, actor: "supplier", whenLabel: `${spec.closedDate} · 11:00`, event: "Supplier accepts",      detail: "Within envelope · confirmation requested" },
            { id: `ae-${spec.id}-5`, actor: "supplier", whenLabel: `${spec.closedDate} · 14:20`, event: "Supplier confirmed",    detail: "Acceptance recorded" },
            { id: `ae-${spec.id}-6`, actor: "agent",    whenLabel: `${spec.closedDate} · 14:22`, event: "Requisition created",   detail: "SAP Ariba · amendment under MSA" },
        ];
        return buildDeal({
            id: spec.id,
            supplier: { ...sup, annualSpend: usd(spec.annualValue) },
            state: { kind: "ClosedWon" },
            statValuePairs: [
                ["Grade", sup.grade ?? "—"],
                ["Rounds", String(spec.rounds)],
                ["Routing", "Auto-execute"],
                ["Closed", spec.closedDate],
            ],
            progressPct: 100,
            envelope: [],
            thread: [],
            auditTrail: audit,
            overviewNote: greenNote(
                "Closed within guardrails",
                `Auto-executed and written back. Savings ${spec.savingsLabel}.`
            ),
            outcome: {
                savingsLabel: spec.savingsLabel,
                savingsSub: spec.savingsSub,
                metrics: [
                    ["Rounds", `${spec.rounds} each side`],
                    ["Routing", "Auto-execute"],
                    ["Closed", spec.closedDate],
                    ["Human time", "0 min"],
                ],
                termChanges: [],
                binding: {
                    method: "amendment_under_master",
                    title: "Bound as an amendment under the existing master",
                    description: "No new contract required. The supplier's confirmation plus the updated PO executed against the master.",
                    files: [],
                },
            },
        });
    });
}

// Synthetic Triage deals for upcoming renewals — gives suppliers with a
// renewal in the next 30 days a real Deal so the negotiation view can render
// the autonomy box, accept pause/resume, and let the user message the
// supplier even before the agent has done outreach.
interface TriageSpec {
    id: SupplierId;
    target: string;
}

function buildTriageDeals(): Deal[] {
    const specs: TriageSpec[] = [
        { id: SUP_HARBOR, target: "−3% catalog · drop auto-renewal" },
        { id: SUP_POLARIS, target: "Hold flat · accessorials cap" },
        { id: SUP_ASPEN, target: "−4% on top SKUs" },
        { id: SUP_BEACON, target: "−3% · audit rights" },
        { id: SUP_COASTLINE, target: "Block auto-renewal · −3% catalog" },
    ];
    return specs.map(({ id, target }) => {
        const sup = suppliers.find((s) => s.id === id)!;
        const window = sup.nextRenewal?.window ?? "—";
        const triggerKind =
            sup.nextRenewal?.kind === "auto_renewal_window"
                ? "Auto-renewal window"
                : sup.nextRenewal?.kind === "price_drift"
                  ? "Price drift"
                  : "Contract expiry";
        const deal = buildDeal({
            id: `deal-${(id as unknown as string).replace(/^sup-/, "")}-upcoming`,
            supplier: sup,
            state: { kind: "Triage" },
            statValuePairs: [
                ["When", window],
                ["Target", target],
                ["Routing", "Pending triage"],
            ],
            progressPct: 0,
            envelope: [],
            thread: [],
            auditTrail: [
                {
                    id: `ae-${id}-upcoming-1`,
                    actor: "agent",
                    whenLabel: "Today · 08:00",
                    event: "Trigger detected",
                    detail: `${triggerKind} · ${window}`,
                },
            ],
            overviewNote: {
                tone: "amber",
                title: "Upcoming renewal",
                detail: `Agent will open outreach before the window (${window.toLowerCase()}).`,
            },
        });
        return { ...deal, resultLabel: window };
    });
}

// Minimal in-flight deals (10 active negotiations) to populate the queue.
// Each gets just enough fixture data to render in lists and the deal page.
interface InFlightSpec {
    id: string;
    supplierId: SupplierId;
    round: number;
    awaiting: "agent" | "supplier";
    triggerDate: string;
    target: string;
}

function buildInFlightDeals(): Deal[] {
    const specs: InFlightSpec[] = [
        { id: "deal-magnolia",   supplierId: SUP_MAGNOLIA,   round: 1, awaiting: "supplier", triggerDate: "May 18", target: "−6% on top SKUs" },
        { id: "deal-sentinel",   supplierId: SUP_SENTINEL,   round: 2, awaiting: "supplier", triggerDate: "May 12", target: "Hold flat, Net 60" },
        { id: "deal-cobalt",     supplierId: SUP_COBALT,     round: 1, awaiting: "supplier", triggerDate: "May 22", target: "−4%, 2% rebate" },
        { id: "deal-cypress",    supplierId: SUP_CYPRESS,    round: 1, awaiting: "supplier", triggerDate: "May 20", target: "Hold flat, Net 45" },
        { id: "deal-northwind",  supplierId: SUP_NORTHWIND,  round: 2, awaiting: "agent",    triggerDate: "May 09", target: "−5%, 36-mo commit" },
        { id: "deal-ironwood",   supplierId: SUP_IRONWOOD,   round: 1, awaiting: "supplier", triggerDate: "May 23", target: "−3%, Net 60" },
        { id: "deal-pacific",    supplierId: SUP_PACIFIC,    round: 2, awaiting: "supplier", triggerDate: "May 14", target: "Cap fuel surcharge at 4%" },
        { id: "deal-quartzline", supplierId: SUP_QUARTZLINE, round: 1, awaiting: "supplier", triggerDate: "May 19", target: "Hold flat, Net 60" },
        { id: "deal-vista",      supplierId: SUP_VISTA,      round: 1, awaiting: "supplier", triggerDate: "May 24", target: "−4%, Net 60" },
        { id: "deal-granite",    supplierId: SUP_GRANITE,    round: 2, awaiting: "agent",    triggerDate: "May 10", target: "Hold flat, drop annual minimum" },
    ];
    return specs.map((spec) => {
        const sup = suppliers.find((s) => s.id === spec.supplierId)!;
        const cat = categories.find((c) => c.id === sup.categoryId)!;
        const annual = sup.annualSpend.amount;
        const stem = `${cat.name} · annual renewal`;
        const audit: AuditEvent[] = [
            { id: `ae-${spec.id}-1`, actor: "agent", whenLabel: `${spec.triggerDate} · 07:00`, event: "Trigger detected",
              detail: `Contract renewal window · ${stem}` },
            { id: `ae-${spec.id}-2`, actor: "agent", whenLabel: `${spec.triggerDate} · 07:01`, event: "Re-indexed spend",
              detail: `18mo AP history (S/4HANA) → $${(annual / 1000).toFixed(1)}K true annual value` },
            { id: `ae-${spec.id}-3`, actor: "agent", whenLabel: `${spec.triggerDate} · 07:02`, event: "Classified · autonomous",
              detail: `Within $150K cap · ${cat.name} policy applies · Tier-${sup.grade ?? "B"} supplier · agent owns the negotiation` },
            { id: `ae-${spec.id}-4`, actor: "agent", whenLabel: `${spec.triggerDate} · 09:30`, event: "Outreach sent",
              detail: `Target: ${spec.target}` },
        ];
        const thread: ThreadItem[] = [
            {
                kind: "email",
                side: "agent",
                from: "Crestwell Partners Procurement",
                whenLabel: `${spec.triggerDate} · 09:30`,
                subject: `${cat.name} — annual renewal review`,
                body: [
                    `Hi, ahead of renewal we'd like to align terms for the coming year. Target asks below.`,
                ],
                terms: [["Target", spec.target]],
                closing: "Happy to discuss — looking forward to your reply.",
            },
        ];
        if (spec.round >= 2) {
            audit.push({
                id: `ae-${spec.id}-5`, actor: "supplier",
                whenLabel: `${spec.triggerDate} · 14:15`, event: "Supplier response received",
                detail: "Counter received · within envelope",
            });
            audit.push({
                id: `ae-${spec.id}-6`, actor: "agent",
                whenLabel: `${spec.triggerDate} · 14:20`, event: "Round 2 sent",
                detail: "Within ladder",
            });
            thread.push({
                kind: "email", side: "supplier",
                from: sup.name, whenLabel: `${spec.triggerDate} · 14:15`,
                subject: `Re: ${cat.name} — annual renewal review`,
                body: ["Thanks. Sharing our counter below — happy to keep talking."],
            });
        }
        return buildDeal({
            id: spec.id,
            supplier: sup,
            state: { kind: "Negotiating", round: spec.round, awaiting: spec.awaiting },
            statValuePairs: [["Grade", sup.grade ?? "—"], ["Round", String(spec.round)]],
            progressPct: spec.round === 1 ? 25 : 55,
            envelope: [],
            thread,
            auditTrail: audit,
            overviewNote: greenNote(
                "Running normally",
                "This deal is progressing inside its envelope. No action needed from you."
            ),
        });
    });
}

const deals: Deal[] = [
    buildDeal({
        id: "deal-crescent",
        supplier: suppliers.find((s) => s.id === SUP_CRESCENT)!,
        state: { kind: "ClosedWon" },
        statValuePairs: [["Grade", "B"], ["Opportunity", "$4.3K – $6.1K"], ["Rounds", "3"], ["Routing", "Auto-execute"], ["Closed", "Oct 31, 2026"]],
        progressPct: 100,
        envelope: crescentEnvelope,
        benchmark: crescentBenchmark,
        thread: crescentThread,
        auditTrail: crescentAudit,
        overviewNote: greenNote(
            "Closed within guardrails",
            "All levers landed inside the envelope. Savings under the $25K MRO auto-execute threshold, so the agent closed and wrote back with no approval needed. You were notified for visibility."
        ),
        outcome: crescentOutcome,
    }),
    buildDeal({
        id: "deal-apex",
        supplier: suppliers.find((s) => s.id === SUP_APEX)!,
        state: { kind: "Escalated", reason: "floor_breach", resumeRound: 2 },
        statValuePairs: [["Grade", "C"], ["Opportunity", "$5.5K – $9.1K"], ["Round", "2 of 3"], ["Status", "Paused"]],
        progressPct: 55,
        envelope: apexEnvelope,
        benchmark: apexBenchmark,
        thread: apexThread,
        auditTrail: apexAudit,
        overviewNote: amberNote(
            "Negotiation paused at your floor",
            "This deal is sitting in your Needs Attention queue. The agent will not act further until you decide. Nothing has been sent to the supplier since the pause."
        ),
        escalation: apexEscalation,
    }),
    buildDeal({
        id: "deal-brightway",
        supplier: suppliers.find((s) => s.id === SUP_BRIGHTWAY)!,
        state: { kind: "Held", reason: "above_autonomous_cap" },
        statValuePairs: [["Annual value", "$310,000"], ["Tail cap", "$150,000"], ["Stage", "Triage"], ["Status", "Held"]],
        progressPct: 15,
        envelope: [],
        thread: brightwayThread,
        auditTrail: brightwayAudit,
        overviewNote: amberNote(
            "Held at triage",
            "The agent never engaged the supplier. It flagged the value mismatch and routed to you. No outreach has been sent."
        ),
        escalation: brightwayEscalation,
    }),
    buildDeal({
        id: "deal-orion",
        supplier: suppliers.find((s) => s.id === SUP_ORION)!,
        state: { kind: "Escalated", reason: "supplier_silent", resumeRound: 1 },
        statValuePairs: [["Grade", "C+"], ["Opportunity", "$2.1K – $3.4K"], ["Round", "1"], ["Status", "Stalled"]],
        progressPct: 30,
        envelope: orionEnvelope,
        thread: orionThread,
        auditTrail: orionAudit,
        overviewNote: amberNote(
            "Stalled on supplier side",
            "Two nudges sent automatically. The agent is now waiting on your decision before any further contact."
        ),
        escalation: orionEscalation,
    }),
    buildDeal({
        id: "deal-cardinal",
        supplier: suppliers.find((s) => s.id === SUP_CARDINAL)!,
        state: { kind: "Negotiating", round: 2, awaiting: "supplier" },
        statValuePairs: [["Grade", "B−"], ["Opportunity", "$3.9K"], ["Round", "2"], ["Status", "Active"]],
        progressPct: 55,
        envelope: cardinalEnvelope,
        thread: cardinalThread,
        auditTrail: cardinalAudit,
        overviewNote: greenNote(
            "Running normally",
            "This deal is progressing inside its envelope. No action needed from you."
        ),
    }),
    buildDeal({
        id: "deal-vertex",
        supplier: suppliers.find((s) => s.id === SUP_VERTEX)!,
        state: { kind: "ClosedWon" },
        statValuePairs: [["Grade", "B"], ["Opportunity", "$2.8K – $3.4K"], ["Rounds", "2"], ["Routing", "Auto-execute"], ["Closed", "Oct 31, 2026"]],
        progressPct: 100,
        envelope: vertexEnvelope,
        benchmark: vertexBenchmark,
        thread: vertexThread,
        auditTrail: vertexAudit,
        overviewNote: greenNote(
            "Closed within guardrails · net-new",
            "A net-new supplier with no prior agreement. Terms landed in-envelope and under the $150K autonomous cap, so the agent issued the PO and wrote back with no approval. You were notified for visibility."
        ),
        outcome: vertexOutcome,
    }),
    buildDeal({
        id: "deal-orbit",
        supplier: suppliers.find((s) => s.id === SUP_ORBIT)!,
        state: { kind: "ClosedWon" },
        statValuePairs: [["Grade", "B"], ["Opportunity", "$3.4K – $4.6K"], ["Rounds", "2"], ["Routing", "Auto-execute"], ["Closed", "Oct 14, 2026"]],
        progressPct: 100,
        envelope: orbitEnvelope,
        thread: orbitThread,
        auditTrail: orbitAudit,
        overviewNote: greenNote(
            "Closed within guardrails",
            "Terms in-envelope, under the $150K cap. Auto-executed and written back. You were notified for visibility."
        ),
        outcome: orbitOutcome,
    }),
    buildDeal({
        id: "deal-delta",
        supplier: suppliers.find((s) => s.id === SUP_DELTA)!,
        state: { kind: "ClosedWon" },
        statValuePairs: [["Grade", "B+"], ["Opportunity", "$1.6K – $2.2K"], ["Rounds", "2"], ["Routing", "Auto-execute"], ["Closed", "Sep 22, 2026"]],
        progressPct: 100,
        envelope: deltaEnvelope,
        thread: deltaThread,
        auditTrail: deltaAudit,
        overviewNote: greenNote(
            "Closed within guardrails",
            "In-envelope and under the cap. Auto-executed. You were notified for visibility."
        ),
        outcome: deltaOutcome,
    }),
    buildDeal({
        id: "deal-nimbus",
        supplier: suppliers.find((s) => s.id === SUP_NIMBUS)!,
        state: { kind: "ClosedWon" },
        statValuePairs: [["Grade", "B"], ["Opportunity", "$5.8K – $8.0K"], ["Rounds", "3"], ["Routing", "E-sign"], ["Closed", "Sep 05, 2026"]],
        progressPct: 100,
        envelope: nimbusEnvelope,
        thread: nimbusThread,
        auditTrail: nimbusAudit,
        overviewNote: greenNote(
            "Closed · routed to you for signature",
            "At $128K this sat in the $100K–$150K e-sign band, so the agent agreed terms and routed the order form to you to countersign before the requisition."
        ),
        outcome: nimbusOutcome,
    }),
    ...buildInFlightDeals(),
    ...buildPastDeals(),
    ...(() => {
        const triage = buildTriageDeals();
        // Wire currentDealId back onto the supplier records so suppliers and
        // the deal queue agree on what's in flight.
        for (const d of triage) {
            const sup = suppliers.find((s) => s.id === d.supplierId);
            if (sup) sup.currentDealId = d.id;
        }
        return triage;
    })(),
];

// ---------------------------------------------------------------------------
// Connectors, notifications, reporting, audit
// ---------------------------------------------------------------------------
const CONN_ARIBA = connectorId("conn-ariba");
const CONN_S4 = connectorId("conn-s4");
const CONN_M365 = connectorId("conn-m365");
const CONN_DS = connectorId("conn-ds");

const connectors: Connector[] = [
    {
        id: CONN_ARIBA,
        system: "sap_ariba",
        logoLabel: "SAP",
        logoColor: "#0a6ed1",
        name: "SAP Ariba",
        detail: "Contract Workspace + Sourcing · daily scan",
        status: "active",
        lastSyncedLabel: "2 minutes ago",
        eventsToday: 38,
        avgLatencyMs: 420,
    },
    {
        id: CONN_S4,
        system: "s4hana",
        logoLabel: "S/4",
        logoColor: "#0a6ed1",
        name: "SAP S/4HANA",
        detail: "Master data + AP · 18mo invoice history",
        status: "active",
        lastSyncedLabel: "8 minutes ago",
        eventsToday: 12,
        avgLatencyMs: 580,
    },
    {
        id: CONN_M365,
        system: "m365_mail",
        logoLabel: "M365",
        logoColor: "#0078d4",
        name: "Microsoft 365 mail",
        detail: "Agent inbox · DKIM verified",
        status: "active",
        lastSyncedLabel: "live",
        eventsToday: 64,
        avgLatencyMs: 180,
    },
    {
        id: CONN_DS,
        system: "docusign",
        logoLabel: "DS",
        logoColor: "#d9a514",
        name: "DocuSign",
        detail: "Amendment + signature routing",
        status: "active",
        lastSyncedLabel: "11 minutes ago",
        eventsToday: 6,
        avgLatencyMs: 310,
    },
];

const ce = (
    id: string,
    connector: ConnectorId,
    direction: ConnectorEventDirection,
    whenLabel: string,
    title: string,
    detail?: string,
    dealRef?: string
): ConnectorEvent => ({
    id,
    connectorId: connector,
    direction,
    whenLabel,
    title,
    detail,
    dealId: dealRef ? dealId(dealRef) : undefined,
});

const systemEvents: ConnectorEvent[] = [
    // SAP Ariba ----------------------------------------------------------
    ce("ev-ariba-1", CONN_ARIBA, "out", "today · 11:42", "Requisition created · PR-2026-118204",
        "Crescent Industrial · amendment under MSA CW-2026-08847", "deal-crescent"),
    ce("ev-ariba-2", CONN_ARIBA, "out", "today · 11:43", "Amendment uploaded · CW-2026-08847",
        "Crescent Industrial · price + payment terms", "deal-crescent"),
    ce("ev-ariba-3", CONN_ARIBA, "in", "today · 06:00", "Supplier master sync · 9,247 records",
        "Nightly delta · 18 new suppliers, 4 deactivations"),
    ce("ev-ariba-4", CONN_ARIBA, "in", "Nov 03 · 06:00", "Renewal trigger · Brightway Logistics",
        "Contract expiry in 58 days · annual freight agreement", "deal-brightway"),
    ce("ev-ariba-5", CONN_ARIBA, "in", "Nov 02 · 06:00", "Renewal trigger · Apex Lab Reagents",
        "Contract expiry in 21 days · annual reagent supply", "deal-apex"),
    ce("ev-ariba-6", CONN_ARIBA, "in", "Nov 01 · 06:00", "Renewal trigger · Cardinal Pallet & Crate",
        "Contract expiry in 34 days · packaging master", "deal-cardinal"),
    ce("ev-ariba-7", CONN_ARIBA, "out", "Oct 31 · 16:38", "PO issued · PO-4500219887",
        "Vertex Safety · net-new PPE basket · standard T&Cs v6", "deal-vertex"),
    ce("ev-ariba-8", CONN_ARIBA, "out", "Oct 31 · 16:36", "Sourcing event closed · PPE basket",
        "Vertex Safety · single award", "deal-vertex"),
    ce("ev-ariba-9", CONN_ARIBA, "in", "Oct 27 · 06:00", "Renewal trigger · Orion Office & Print",
        "Contract expiry in 12 days · office supplies", "deal-orion"),
    ce("ev-ariba-10", CONN_ARIBA, "out", "Oct 14 · 13:07", "Requisition created · PR-2026-117103",
        "Orbit Janitorial · amendment under MSA", "deal-orbit"),

    // SAP S/4HANA --------------------------------------------------------
    ce("ev-s4-1", CONN_S4, "in", "today · 04:30", "AP batch ingested · 4,218 invoices",
        "Nightly delta · spend snapshot updated"),
    ce("ev-s4-2", CONN_S4, "in", "Nov 03 · 07:41", "Spend re-index complete · Brightway",
        "18mo history → $310K true annual value", "deal-brightway"),
    ce("ev-s4-3", CONN_S4, "out", "Nov 03 · 07:40", "Spend re-index requested · Brightway",
        "Triggered after renewal alert", "deal-brightway"),
    ce("ev-s4-4", CONN_S4, "in", "Nov 02 · 08:12", "Spend re-index complete · Apex Lab Reagents",
        "18mo history → $92.3K true annual value", "deal-apex"),
    ce("ev-s4-5", CONN_S4, "out", "Nov 02 · 08:11", "Spend re-index requested · Apex Lab Reagents",
        "Triggered after renewal alert", "deal-apex"),
    ce("ev-s4-6", CONN_S4, "out", "Oct 28 · 07:01", "Master data lookup · Vertex 12-SKU basket",
        "Net-new sourcing · category benchmarks pulled", "deal-vertex"),
    ce("ev-s4-7", CONN_S4, "in", "Oct 27 · 04:00", "Master data refresh",
        "18 SKU adds, 6 deletions across tail categories"),
    ce("ev-s4-8", CONN_S4, "in", "Mon · 04:00", "Spend snapshot · weekly delta",
        "Top-10 tail categories refreshed"),

    // Microsoft 365 mail -------------------------------------------------
    ce("ev-m365-1", CONN_M365, "in", "Nov 04 · 13:30", "Reply received · Apex Lab Reagents",
        "Counter on price + payment terms · −4% / Net 30", "deal-apex"),
    ce("ev-m365-2", CONN_M365, "out", "Nov 03 · 09:20", "Round 2 sent · Cardinal Pallet & Crate",
        "Within ladder", "deal-cardinal"),
    ce("ev-m365-3", CONN_M365, "out", "Nov 02 · 10:02", "Outreach sent · Apex Lab Reagents",
        "Initial email · 3 levers, target asks", "deal-apex"),
    ce("ev-m365-4", CONN_M365, "out", "Nov 02 · 09:00", "Follow-up 2 sent · Orion Office & Print",
        "Automated nudge · 7 days since outreach", "deal-orion"),
    ce("ev-m365-5", CONN_M365, "in", "Oct 31 · 11:38", "Reply received · Crescent Industrial",
        "Confirmation · \"Confirmed. Send it over.\"", "deal-crescent"),
    ce("ev-m365-6", CONN_M365, "out", "Oct 31 · 11:24", "Confirmation requested · Crescent Industrial",
        "Structured terms recap for explicit confirm", "deal-crescent"),
    ce("ev-m365-7", CONN_M365, "in", "Oct 29 · 16:48", "Reply received · Crescent Industrial",
        "Counter on price, payment terms, rebate", "deal-crescent"),
    ce("ev-m365-8", CONN_M365, "out", "Oct 28 · 09:14", "Outreach sent · Crescent Industrial",
        "Initial email · 3 levers, target asks", "deal-crescent"),
    ce("ev-m365-9", CONN_M365, "out", "Oct 30 · 09:05", "Round 2 sent · Vertex Safety Equip.",
        "Target −6% vs quote, Net 60", "deal-vertex"),

    // DocuSign -----------------------------------------------------------
    ce("ev-ds-1", CONN_DS, "in", "today · 15:22", "Envelope completed · CFE-44218",
        "Crescent amendment · signed by Marcus Whelan", "deal-crescent"),
    ce("ev-ds-2", CONN_DS, "out", "today · 14:00", "Reminder sent · Crescent (Day 1)",
        "Auto-reminder · awaiting Marcus Whelan"),
    ce("ev-ds-3", CONN_DS, "in", "today · 11:50", "Recipient viewed · Crescent amendment",
        "Marcus Whelan opened the envelope", "deal-crescent"),
    ce("ev-ds-4", CONN_DS, "out", "today · 11:43", "Envelope sent · Crescent amendment",
        "CFE-44218 · Marcus Whelan + Diogo Passos", "deal-crescent"),
    ce("ev-ds-5", CONN_DS, "in", "Sep 05 · 10:15", "Envelope completed · CFE-44217",
        "Nimbus IT order form · countersigned by Diogo Passos", "deal-nimbus"),
    ce("ev-ds-6", CONN_DS, "out", "Sep 04 · 16:32", "Envelope sent · Nimbus IT order form",
        "CFE-44217 · for Diogo Passos to countersign", "deal-nimbus"),
];

const notifications: Notification[] = [
    {
        id: "n-apex",
        severity: "critical",
        title: "Apex Lab Reagents needs a decision",
        subtitle: "Counter +4% exceeds your +2% ceiling",
        whenLabel: "2 hours ago",
        dealId: dealId("deal-apex"),
        read: false,
    },
    {
        id: "n-brightway",
        severity: "warn",
        title: "Brightway Logistics above tail cap",
        subtitle: "$310K re-indexed · reassign to strategic",
        whenLabel: "5 hours ago",
        dealId: dealId("deal-brightway"),
        read: false,
    },
    {
        id: "n-orion",
        severity: "warn",
        title: "Orion Office & Print non-responsive",
        subtitle: "No reply after 2 nudges over 7 days",
        whenLabel: "1 day ago",
        dealId: dealId("deal-orion"),
        read: false,
    },
    {
        id: "n-crescent",
        severity: "success",
        title: "Crescent Industrial closed",
        subtitle: "−$4,287/yr · auto-executed · written back to Ariba",
        whenLabel: "3 hours ago",
        dealId: dealId("deal-crescent"),
        read: true,
    },
    {
        id: "n-vertex",
        severity: "success",
        title: "Vertex Safety closed",
        subtitle: "−$3,110/yr · net-new PO issued",
        whenLabel: "6 hours ago",
        dealId: dealId("deal-vertex"),
        read: true,
    },
];

const report: PortfolioReport = {
    kpis: [
        { label: "Value captured · YTD", value: "$2.4M", direction: "up", detail: "↑ 22% vs last year" },
        { label: "Avg cycle time", value: "5.0 days", direction: "up", detail: "vs ~41 days manual" },
        { label: "Tail coverage", value: "80%", direction: "up", detail: "of addressable tail in scope" },
        { label: "Supplier satisfaction", value: "4.5 / 5", direction: "flat", detail: "84% rated the process good+" },
    ],
    kpiTrends: [
        {
            label: "Value captured",
            color: "#8FB8DD",
            format: "currency-auto",
            // Running sum of the monthly savings — Dec '24 through May 2026.
            values: [80, 170, 270, 385, 515, 655, 806, 978, 1169, 1379, 1624, 1859, 2149, 2470, 2775, 3155, 3564, 4004],
        },
        {
            label: "Avg cycle time",
            color: "#8FB8DD",
            format: "days",
            values: [7.4, 7.2, 7.1, 6.9, 6.8, 6.6, 6.4, 6.2, 6.1, 5.9, 5.8, 5.6, 5.5, 5.4, 5.3, 5.2, 5.1, 5.0],
        },
        {
            label: "Tail coverage",
            color: "#8FB8DD",
            format: "percent",
            values: [60, 61, 63, 64, 66, 67, 68, 70, 71, 73, 74, 75, 76, 77, 78, 78, 79, 80],
        },
        {
            label: "Supplier satisfaction",
            color: "#8FB8DD",
            format: "rating",
            values: [4.0, 4.1, 4.1, 4.2, 4.2, 4.2, 4.3, 4.3, 4.3, 4.4, 4.4, 4.4, 4.4, 4.4, 4.5, 4.5, 4.5, 4.5],
        },
    ],
    // 18 months ending in May 2026 (the demo's "current" month).
    months: [
        "Dec '24", "Jan '25", "Feb '25", "Mar '25", "Apr '25", "May '25",
        "Jun '25", "Jul '25", "Aug '25", "Sep '25", "Oct '25", "Nov '25", "Dec '25",
        "Jan", "Feb", "Mar", "Apr", "May",
    ],
    // Palette: monochromatic blue ramp — lightest band (largest cat) at the
    // bottom of each stack, darkening toward the top.
    valueByCategory: [
        { name: "MRO & Indirect", color: "#D6E4F0", values: [26, 30, 33, 38, 43, 46, 50, 56, 63, 70, 81, 77, 96, 106, 101, 126, 135, 145] },
        { name: "Packaging", color: "#B3CDE3", values: [14, 16, 18, 21, 23, 25, 27, 31, 34, 38, 44, 42, 52, 57, 55, 68, 73, 79] },
        { name: "Freight & Logistics", color: "#8FB8DD", values: [14, 15, 17, 20, 22, 24, 26, 29, 32, 36, 42, 40, 49, 54, 52, 65, 70, 75] },
        { name: "R&D Consumables", color: "#6B9BCA", values: [10, 11, 12, 14, 16, 17, 18, 20, 23, 25, 29, 28, 35, 38, 36, 46, 49, 53] },
        { name: "PPE / Safety", color: "#4A7CB0", values: [8, 9, 10, 11, 13, 14, 15, 17, 19, 21, 25, 24, 29, 32, 30, 38, 41, 44] },
        { name: "Other", color: "#2E5C8A", values: [8, 9, 10, 11, 13, 14, 15, 17, 19, 20, 24, 24, 29, 33, 31, 37, 41, 44] },
    ],
    outcomeRateByMonth: [
        { name: "Auto-executed", color: "#D6E4F0", values: [30, 32, 34, 35, 36, 37, 38, 42, 45, 48, 50, 55, 58, 62, 64, 66, 68, 70] },
        { name: "Buyer e-signed", color: "#8FB8DD", values: [28, 28, 27, 27, 27, 26, 25, 24, 23, 22, 22, 20, 19, 18, 18, 17, 17, 16] },
        { name: "Escalated · auto-resumed", color: "#6B9BCA", values: [28, 27, 26, 26, 25, 24, 24, 22, 21, 20, 18, 16, 14, 12, 12, 11, 10, 10] },
        { name: "Escalated to you", color: "#4A7CB0", values: [9, 8, 8, 8, 8, 8, 8, 7, 7, 6, 6, 6, 5, 5, 4, 4, 3, 3] },
        { name: "Re-sourced / walked", color: "#2E5C8A", values: [5, 5, 5, 4, 4, 5, 5, 5, 4, 4, 4, 3, 4, 3, 2, 2, 2, 1] },
    ],
};

// ---------------------------------------------------------------------------
// Decision history — exceptions you've already resolved
// ---------------------------------------------------------------------------
const decisionHistory: DecisionResolution[] = [
    {
        chosenOptionId: "hist-nimbus",
        chosenOptionTitle: "Reviewed and countersigned",
        decidedByName: "Diogo Passos",
        decidedAtLabel: "Sep 5",
        result: "Closed −5.3%",
        supplierName: "Nimbus IT Reseller",
        issue: "Value in the $100K–$150K e-sign band",
        resultTone: "green",
        dealId: dealId("deal-nimbus"),
    },
    {
        chosenOptionId: "hist-crescent-2025",
        chosenOptionTitle: "Accepted with a 12-month price cap",
        decidedByName: "Diogo Passos",
        decidedAtLabel: "Dec 14, 2025",
        result: "Closed −4.7%",
        supplierName: "Crescent Industrial Supply",
        issue: "Counter +3% exceeded the price ceiling",
        resultTone: "green",
        dealId: dealId("deal-crescent-2025"),
    },
    {
        chosenOptionId: "hist-orbit-2025",
        chosenOptionTitle: "Accepted auto-renewal terms",
        decidedByName: "Diogo Passos",
        decidedAtLabel: "Sep 18, 2025",
        result: "Closed −4.4%",
        supplierName: "Orbit Janitorial",
        issue: "Auto-renewal in the e-sign band",
        resultTone: "green",
        dealId: dealId("deal-orbit-2025-sep"),
    },
    {
        chosenOptionId: "hist-orion-2024",
        chosenOptionTitle: "Sent a personal note, agent resumed",
        decidedByName: "Diogo Passos",
        decidedAtLabel: "Nov 18, 2024",
        result: "Closed −3.1%",
        supplierName: "Orion Office & Print",
        issue: "Supplier non-responsive after 2 nudges",
        resultTone: "green",
        dealId: dealId("deal-orion-2024"),
    },
    {
        chosenOptionId: "hist-delta-2025",
        chosenOptionTitle: "Held the ceiling, supplier accepted",
        decidedByName: "Diogo Passos",
        decidedAtLabel: "Mar 12, 2025",
        result: "Closed −3.7%",
        supplierName: "Delta Calibration",
        issue: "Counter +5% vs the +2% ceiling",
        resultTone: "green",
        dealId: dealId("deal-delta-2025"),
    },
];

// ---------------------------------------------------------------------------
// Workspace seed
// ---------------------------------------------------------------------------
export function seedWorkspace(): Workspace {
    return {
        org: { id: "org-cp", name: "Crestwell Partners", unit: "Global Procurement" },
        currentUser: { id: "user-dp", name: "Diogo Passos", role: "Category Lead · Indirect" },
        suppliersInScope: 9247,
        categories,
        policies,
        suppliers,
        deals,
        connectors,
        systemEvents,
        notifications,
        report,
        decisionHistory,
    };
}

export const TONE_BY_VARIANT: Record<"agreement" | "ok" | "warn", Tone> = {
    agreement: "green",
    ok: "blue",
    warn: "amber",
};
